// src/Compositor/Compositor.ts
import * as THREE4 from "three";

// src/helpers.ts
var hasOwnProperty = (object, property) => Object.prototype.hasOwnProperty.call(object, property);
var hasDimensions = (texture) => {
  const image = texture.image;
  return !!image && typeof image === "object" && hasOwnProperty(image, "width") && hasOwnProperty(image, "height") && typeof image.width === "number" && typeof image.height === "number";
};
var toSimpleCoordinates = ({
  x,
  y
}) => ({ x, y });
var flipImageData = (data, imageData) => {
  const { width, height } = imageData;
  for (let y = 0; y < height; y++) {
    const row = 4 * width;
    const flippedY = height - y - 1;
    const sourceOffset = row * flippedY;
    const destinationOffset = row * y;
    imageData.data.set(
      data.subarray(sourceOffset, sourceOffset + row),
      destinationOffset
    );
  }
};

// src/Compositor/EventDispatcher.ts
var EventDispatcher = class {
  constructor() {
    this._listeners = {};
  }
  addEventListener(type, listener) {
    if (this._listeners === void 0)
      this._listeners = {};
    const listeners = this._listeners;
    if (listeners[type] === void 0) {
      listeners[type] = [];
    }
    if (listeners[type].indexOf(listener) === -1) {
      listeners[type].push(listener);
    }
  }
  hasEventListener(type, listener) {
    if (this._listeners === void 0)
      return false;
    const listeners = this._listeners;
    return listeners[type] !== void 0 && listeners[type].indexOf(listener) !== -1;
  }
  removeEventListener(type, listener) {
    if (this._listeners === void 0)
      return;
    const listeners = this._listeners;
    const listenerArray = listeners[type];
    if (listenerArray !== void 0) {
      const index = listenerArray.indexOf(listener);
      if (index !== -1) {
        listenerArray.splice(index, 1);
      }
    }
  }
  dispatchEvent(event) {
    if (this._listeners === void 0)
      return;
    const listeners = this._listeners;
    const listenerArray = listeners[event.type];
    if (listenerArray !== void 0) {
      event.target = this;
      const array = listenerArray.slice(0);
      for (let i = 0, l = array.length; i < l; i++) {
        array[i].call(this, event);
      }
      event.target = null;
    }
  }
};

// src/Compositor/SceneController.ts
import { ensureError } from "@hover/utilities";
import BigNumber from "bignumber.js";
import * as THREE3 from "three";

// ../../node_modules/three/examples/jsm/loaders/EXRLoader.js
import {
  DataTextureLoader,
  DataUtils,
  FloatType,
  HalfFloatType,
  LinearEncoding,
  LinearFilter,
  RedFormat,
  RGBAFormat
} from "three";

// ../../node_modules/three/examples/jsm/libs/fflate.module.js
var durl = function(c) {
  return URL.createObjectURL(new Blob([c], { type: "text/javascript" }));
};
var cwk = function(u) {
  return new Worker(u);
};
try {
  URL.revokeObjectURL(durl(""));
} catch (e) {
  durl = function(c) {
    return "data:application/javascript;charset=UTF-8," + encodeURI(c);
  };
  cwk = function(u) {
    return new Worker(u, { type: "module" });
  };
}
var u8 = Uint8Array;
var u16 = Uint16Array;
var u32 = Uint32Array;
var fleb = new u8([
  0,
  0,
  0,
  0,
  0,
  0,
  0,
  0,
  1,
  1,
  1,
  1,
  2,
  2,
  2,
  2,
  3,
  3,
  3,
  3,
  4,
  4,
  4,
  4,
  5,
  5,
  5,
  5,
  0,
  /* unused */
  0,
  0,
  /* impossible */
  0
]);
var fdeb = new u8([
  0,
  0,
  0,
  0,
  1,
  1,
  2,
  2,
  3,
  3,
  4,
  4,
  5,
  5,
  6,
  6,
  7,
  7,
  8,
  8,
  9,
  9,
  10,
  10,
  11,
  11,
  12,
  12,
  13,
  13,
  /* unused */
  0,
  0
]);
var clim = new u8([16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15]);
var freb = function(eb, start) {
  var b = new u16(31);
  for (var i = 0; i < 31; ++i) {
    b[i] = start += 1 << eb[i - 1];
  }
  var r = new u32(b[30]);
  for (var i = 1; i < 30; ++i) {
    for (var j = b[i]; j < b[i + 1]; ++j) {
      r[j] = j - b[i] << 5 | i;
    }
  }
  return [b, r];
};
var _a = freb(fleb, 2);
var fl = _a[0];
var revfl = _a[1];
fl[28] = 258, revfl[258] = 28;
var _b = freb(fdeb, 0);
var fd = _b[0];
var revfd = _b[1];
var rev = new u16(32768);
for (i = 0; i < 32768; ++i) {
  x = (i & 43690) >>> 1 | (i & 21845) << 1;
  x = (x & 52428) >>> 2 | (x & 13107) << 2;
  x = (x & 61680) >>> 4 | (x & 3855) << 4;
  rev[i] = ((x & 65280) >>> 8 | (x & 255) << 8) >>> 1;
}
var x;
var i;
var hMap = function(cd, mb, r) {
  var s = cd.length;
  var i = 0;
  var l = new u16(mb);
  for (; i < s; ++i)
    ++l[cd[i] - 1];
  var le = new u16(mb);
  for (i = 0; i < mb; ++i) {
    le[i] = le[i - 1] + l[i - 1] << 1;
  }
  var co;
  if (r) {
    co = new u16(1 << mb);
    var rvb = 15 - mb;
    for (i = 0; i < s; ++i) {
      if (cd[i]) {
        var sv = i << 4 | cd[i];
        var r_1 = mb - cd[i];
        var v = le[cd[i] - 1]++ << r_1;
        for (var m = v | (1 << r_1) - 1; v <= m; ++v) {
          co[rev[v] >>> rvb] = sv;
        }
      }
    }
  } else {
    co = new u16(s);
    for (i = 0; i < s; ++i) {
      if (cd[i]) {
        co[i] = rev[le[cd[i] - 1]++] >>> 15 - cd[i];
      }
    }
  }
  return co;
};
var flt = new u8(288);
for (i = 0; i < 144; ++i)
  flt[i] = 8;
var i;
for (i = 144; i < 256; ++i)
  flt[i] = 9;
var i;
for (i = 256; i < 280; ++i)
  flt[i] = 7;
var i;
for (i = 280; i < 288; ++i)
  flt[i] = 8;
var i;
var fdt = new u8(32);
for (i = 0; i < 32; ++i)
  fdt[i] = 5;
var i;
var flrm = /* @__PURE__ */ hMap(flt, 9, 1);
var fdrm = /* @__PURE__ */ hMap(fdt, 5, 1);
var max = function(a) {
  var m = a[0];
  for (var i = 1; i < a.length; ++i) {
    if (a[i] > m)
      m = a[i];
  }
  return m;
};
var bits = function(d, p, m) {
  var o = p / 8 | 0;
  return (d[o] | d[o + 1] << 8) >> (p & 7) & m;
};
var bits16 = function(d, p) {
  var o = p / 8 | 0;
  return (d[o] | d[o + 1] << 8 | d[o + 2] << 16) >> (p & 7);
};
var shft = function(p) {
  return (p / 8 | 0) + (p & 7 && 1);
};
var slc = function(v, s, e) {
  if (s == null || s < 0)
    s = 0;
  if (e == null || e > v.length)
    e = v.length;
  var n = new (v instanceof u16 ? u16 : v instanceof u32 ? u32 : u8)(e - s);
  n.set(v.subarray(s, e));
  return n;
};
var inflt = function(dat, buf, st) {
  var sl = dat.length;
  if (!sl || st && !st.l && sl < 5)
    return buf || new u8(0);
  var noBuf = !buf || st;
  var noSt = !st || st.i;
  if (!st)
    st = {};
  if (!buf)
    buf = new u8(sl * 3);
  var cbuf = function(l2) {
    var bl = buf.length;
    if (l2 > bl) {
      var nbuf = new u8(Math.max(bl * 2, l2));
      nbuf.set(buf);
      buf = nbuf;
    }
  };
  var final = st.f || 0, pos = st.p || 0, bt = st.b || 0, lm = st.l, dm = st.d, lbt = st.m, dbt = st.n;
  var tbts = sl * 8;
  do {
    if (!lm) {
      st.f = final = bits(dat, pos, 1);
      var type = bits(dat, pos + 1, 3);
      pos += 3;
      if (!type) {
        var s = shft(pos) + 4, l = dat[s - 4] | dat[s - 3] << 8, t = s + l;
        if (t > sl) {
          if (noSt)
            throw "unexpected EOF";
          break;
        }
        if (noBuf)
          cbuf(bt + l);
        buf.set(dat.subarray(s, t), bt);
        st.b = bt += l, st.p = pos = t * 8;
        continue;
      } else if (type == 1)
        lm = flrm, dm = fdrm, lbt = 9, dbt = 5;
      else if (type == 2) {
        var hLit = bits(dat, pos, 31) + 257, hcLen = bits(dat, pos + 10, 15) + 4;
        var tl = hLit + bits(dat, pos + 5, 31) + 1;
        pos += 14;
        var ldt = new u8(tl);
        var clt = new u8(19);
        for (var i = 0; i < hcLen; ++i) {
          clt[clim[i]] = bits(dat, pos + i * 3, 7);
        }
        pos += hcLen * 3;
        var clb = max(clt), clbmsk = (1 << clb) - 1;
        var clm = hMap(clt, clb, 1);
        for (var i = 0; i < tl; ) {
          var r = clm[bits(dat, pos, clbmsk)];
          pos += r & 15;
          var s = r >>> 4;
          if (s < 16) {
            ldt[i++] = s;
          } else {
            var c = 0, n = 0;
            if (s == 16)
              n = 3 + bits(dat, pos, 3), pos += 2, c = ldt[i - 1];
            else if (s == 17)
              n = 3 + bits(dat, pos, 7), pos += 3;
            else if (s == 18)
              n = 11 + bits(dat, pos, 127), pos += 7;
            while (n--)
              ldt[i++] = c;
          }
        }
        var lt = ldt.subarray(0, hLit), dt = ldt.subarray(hLit);
        lbt = max(lt);
        dbt = max(dt);
        lm = hMap(lt, lbt, 1);
        dm = hMap(dt, dbt, 1);
      } else
        throw "invalid block type";
      if (pos > tbts) {
        if (noSt)
          throw "unexpected EOF";
        break;
      }
    }
    if (noBuf)
      cbuf(bt + 131072);
    var lms = (1 << lbt) - 1, dms = (1 << dbt) - 1;
    var lpos = pos;
    for (; ; lpos = pos) {
      var c = lm[bits16(dat, pos) & lms], sym = c >>> 4;
      pos += c & 15;
      if (pos > tbts) {
        if (noSt)
          throw "unexpected EOF";
        break;
      }
      if (!c)
        throw "invalid length/literal";
      if (sym < 256)
        buf[bt++] = sym;
      else if (sym == 256) {
        lpos = pos, lm = null;
        break;
      } else {
        var add = sym - 254;
        if (sym > 264) {
          var i = sym - 257, b = fleb[i];
          add = bits(dat, pos, (1 << b) - 1) + fl[i];
          pos += b;
        }
        var d = dm[bits16(dat, pos) & dms], dsym = d >>> 4;
        if (!d)
          throw "invalid distance";
        pos += d & 15;
        var dt = fd[dsym];
        if (dsym > 3) {
          var b = fdeb[dsym];
          dt += bits16(dat, pos) & (1 << b) - 1, pos += b;
        }
        if (pos > tbts) {
          if (noSt)
            throw "unexpected EOF";
          break;
        }
        if (noBuf)
          cbuf(bt + 131072);
        var end = bt + add;
        for (; bt < end; bt += 4) {
          buf[bt] = buf[bt - dt];
          buf[bt + 1] = buf[bt + 1 - dt];
          buf[bt + 2] = buf[bt + 2 - dt];
          buf[bt + 3] = buf[bt + 3 - dt];
        }
        bt = end;
      }
    }
    st.l = lm, st.p = lpos, st.b = bt;
    if (lm)
      final = 1, st.m = lbt, st.d = dm, st.n = dbt;
  } while (!final);
  return bt == buf.length ? buf : slc(buf, 0, bt);
};
var et = /* @__PURE__ */ new u8(0);
var zlv = function(d) {
  if ((d[0] & 15) != 8 || d[0] >>> 4 > 7 || (d[0] << 8 | d[1]) % 31)
    throw "invalid zlib data";
  if (d[1] & 32)
    throw "invalid zlib data: preset dictionaries not supported";
};
function unzlibSync(data, out) {
  return inflt((zlv(data), data.subarray(2, -4)), out);
}
var td = typeof TextDecoder != "undefined" && /* @__PURE__ */ new TextDecoder();
var tds = 0;
try {
  td.decode(et, { stream: true });
  tds = 1;
} catch (e) {
}

// ../../node_modules/three/examples/jsm/loaders/EXRLoader.js
var EXRLoader = class extends DataTextureLoader {
  constructor(manager) {
    super(manager);
    this.type = HalfFloatType;
  }
  parse(buffer) {
    const USHORT_RANGE = 1 << 16;
    const BITMAP_SIZE = USHORT_RANGE >> 3;
    const HUF_ENCBITS = 16;
    const HUF_DECBITS = 14;
    const HUF_ENCSIZE = (1 << HUF_ENCBITS) + 1;
    const HUF_DECSIZE = 1 << HUF_DECBITS;
    const HUF_DECMASK = HUF_DECSIZE - 1;
    const NBITS = 16;
    const A_OFFSET = 1 << NBITS - 1;
    const MOD_MASK = (1 << NBITS) - 1;
    const SHORT_ZEROCODE_RUN = 59;
    const LONG_ZEROCODE_RUN = 63;
    const SHORTEST_LONG_RUN = 2 + LONG_ZEROCODE_RUN - SHORT_ZEROCODE_RUN;
    const ULONG_SIZE = 8;
    const FLOAT32_SIZE = 4;
    const INT32_SIZE = 4;
    const INT16_SIZE = 2;
    const INT8_SIZE = 1;
    const STATIC_HUFFMAN = 0;
    const DEFLATE = 1;
    const UNKNOWN = 0;
    const LOSSY_DCT = 1;
    const RLE = 2;
    const logBase = Math.pow(2.7182818, 2.2);
    function reverseLutFromBitmap(bitmap, lut) {
      let k = 0;
      for (let i = 0; i < USHORT_RANGE; ++i) {
        if (i == 0 || bitmap[i >> 3] & 1 << (i & 7)) {
          lut[k++] = i;
        }
      }
      const n = k - 1;
      while (k < USHORT_RANGE)
        lut[k++] = 0;
      return n;
    }
    function hufClearDecTable(hdec) {
      for (let i = 0; i < HUF_DECSIZE; i++) {
        hdec[i] = {};
        hdec[i].len = 0;
        hdec[i].lit = 0;
        hdec[i].p = null;
      }
    }
    const getBitsReturn = { l: 0, c: 0, lc: 0 };
    function getBits(nBits, c, lc, uInt8Array2, inOffset) {
      while (lc < nBits) {
        c = c << 8 | parseUint8Array(uInt8Array2, inOffset);
        lc += 8;
      }
      lc -= nBits;
      getBitsReturn.l = c >> lc & (1 << nBits) - 1;
      getBitsReturn.c = c;
      getBitsReturn.lc = lc;
    }
    const hufTableBuffer = new Array(59);
    function hufCanonicalCodeTable(hcode) {
      for (let i = 0; i <= 58; ++i)
        hufTableBuffer[i] = 0;
      for (let i = 0; i < HUF_ENCSIZE; ++i)
        hufTableBuffer[hcode[i]] += 1;
      let c = 0;
      for (let i = 58; i > 0; --i) {
        const nc = c + hufTableBuffer[i] >> 1;
        hufTableBuffer[i] = c;
        c = nc;
      }
      for (let i = 0; i < HUF_ENCSIZE; ++i) {
        const l = hcode[i];
        if (l > 0)
          hcode[i] = l | hufTableBuffer[l]++ << 6;
      }
    }
    function hufUnpackEncTable(uInt8Array2, inOffset, ni, im, iM, hcode) {
      const p = inOffset;
      let c = 0;
      let lc = 0;
      for (; im <= iM; im++) {
        if (p.value - inOffset.value > ni)
          return false;
        getBits(6, c, lc, uInt8Array2, p);
        const l = getBitsReturn.l;
        c = getBitsReturn.c;
        lc = getBitsReturn.lc;
        hcode[im] = l;
        if (l == LONG_ZEROCODE_RUN) {
          if (p.value - inOffset.value > ni) {
            throw new Error("Something wrong with hufUnpackEncTable");
          }
          getBits(8, c, lc, uInt8Array2, p);
          let zerun = getBitsReturn.l + SHORTEST_LONG_RUN;
          c = getBitsReturn.c;
          lc = getBitsReturn.lc;
          if (im + zerun > iM + 1) {
            throw new Error("Something wrong with hufUnpackEncTable");
          }
          while (zerun--)
            hcode[im++] = 0;
          im--;
        } else if (l >= SHORT_ZEROCODE_RUN) {
          let zerun = l - SHORT_ZEROCODE_RUN + 2;
          if (im + zerun > iM + 1) {
            throw new Error("Something wrong with hufUnpackEncTable");
          }
          while (zerun--)
            hcode[im++] = 0;
          im--;
        }
      }
      hufCanonicalCodeTable(hcode);
    }
    function hufLength(code) {
      return code & 63;
    }
    function hufCode(code) {
      return code >> 6;
    }
    function hufBuildDecTable(hcode, im, iM, hdecod) {
      for (; im <= iM; im++) {
        const c = hufCode(hcode[im]);
        const l = hufLength(hcode[im]);
        if (c >> l) {
          throw new Error("Invalid table entry");
        }
        if (l > HUF_DECBITS) {
          const pl = hdecod[c >> l - HUF_DECBITS];
          if (pl.len) {
            throw new Error("Invalid table entry");
          }
          pl.lit++;
          if (pl.p) {
            const p = pl.p;
            pl.p = new Array(pl.lit);
            for (let i = 0; i < pl.lit - 1; ++i) {
              pl.p[i] = p[i];
            }
          } else {
            pl.p = new Array(1);
          }
          pl.p[pl.lit - 1] = im;
        } else if (l) {
          let plOffset = 0;
          for (let i = 1 << HUF_DECBITS - l; i > 0; i--) {
            const pl = hdecod[(c << HUF_DECBITS - l) + plOffset];
            if (pl.len || pl.p) {
              throw new Error("Invalid table entry");
            }
            pl.len = l;
            pl.lit = im;
            plOffset++;
          }
        }
      }
      return true;
    }
    const getCharReturn = { c: 0, lc: 0 };
    function getChar(c, lc, uInt8Array2, inOffset) {
      c = c << 8 | parseUint8Array(uInt8Array2, inOffset);
      lc += 8;
      getCharReturn.c = c;
      getCharReturn.lc = lc;
    }
    const getCodeReturn = { c: 0, lc: 0 };
    function getCode(po, rlc, c, lc, uInt8Array2, inOffset, outBuffer, outBufferOffset, outBufferEndOffset) {
      if (po == rlc) {
        if (lc < 8) {
          getChar(c, lc, uInt8Array2, inOffset);
          c = getCharReturn.c;
          lc = getCharReturn.lc;
        }
        lc -= 8;
        let cs = c >> lc;
        cs = new Uint8Array([cs])[0];
        if (outBufferOffset.value + cs > outBufferEndOffset) {
          return false;
        }
        const s = outBuffer[outBufferOffset.value - 1];
        while (cs-- > 0) {
          outBuffer[outBufferOffset.value++] = s;
        }
      } else if (outBufferOffset.value < outBufferEndOffset) {
        outBuffer[outBufferOffset.value++] = po;
      } else {
        return false;
      }
      getCodeReturn.c = c;
      getCodeReturn.lc = lc;
    }
    function UInt16(value) {
      return value & 65535;
    }
    function Int16(value) {
      const ref = UInt16(value);
      return ref > 32767 ? ref - 65536 : ref;
    }
    const wdec14Return = { a: 0, b: 0 };
    function wdec14(l, h) {
      const ls = Int16(l);
      const hs = Int16(h);
      const hi = hs;
      const ai = ls + (hi & 1) + (hi >> 1);
      const as = ai;
      const bs = ai - hi;
      wdec14Return.a = as;
      wdec14Return.b = bs;
    }
    function wdec16(l, h) {
      const m = UInt16(l);
      const d = UInt16(h);
      const bb = m - (d >> 1) & MOD_MASK;
      const aa = d + bb - A_OFFSET & MOD_MASK;
      wdec14Return.a = aa;
      wdec14Return.b = bb;
    }
    function wav2Decode(buffer2, j, nx, ox, ny, oy, mx) {
      const w14 = mx < 1 << 14;
      const n = nx > ny ? ny : nx;
      let p = 1;
      let p2;
      let py;
      while (p <= n)
        p <<= 1;
      p >>= 1;
      p2 = p;
      p >>= 1;
      while (p >= 1) {
        py = 0;
        const ey = py + oy * (ny - p2);
        const oy1 = oy * p;
        const oy2 = oy * p2;
        const ox1 = ox * p;
        const ox2 = ox * p2;
        let i00, i01, i10, i11;
        for (; py <= ey; py += oy2) {
          let px = py;
          const ex = py + ox * (nx - p2);
          for (; px <= ex; px += ox2) {
            const p01 = px + ox1;
            const p10 = px + oy1;
            const p11 = p10 + ox1;
            if (w14) {
              wdec14(buffer2[px + j], buffer2[p10 + j]);
              i00 = wdec14Return.a;
              i10 = wdec14Return.b;
              wdec14(buffer2[p01 + j], buffer2[p11 + j]);
              i01 = wdec14Return.a;
              i11 = wdec14Return.b;
              wdec14(i00, i01);
              buffer2[px + j] = wdec14Return.a;
              buffer2[p01 + j] = wdec14Return.b;
              wdec14(i10, i11);
              buffer2[p10 + j] = wdec14Return.a;
              buffer2[p11 + j] = wdec14Return.b;
            } else {
              wdec16(buffer2[px + j], buffer2[p10 + j]);
              i00 = wdec14Return.a;
              i10 = wdec14Return.b;
              wdec16(buffer2[p01 + j], buffer2[p11 + j]);
              i01 = wdec14Return.a;
              i11 = wdec14Return.b;
              wdec16(i00, i01);
              buffer2[px + j] = wdec14Return.a;
              buffer2[p01 + j] = wdec14Return.b;
              wdec16(i10, i11);
              buffer2[p10 + j] = wdec14Return.a;
              buffer2[p11 + j] = wdec14Return.b;
            }
          }
          if (nx & p) {
            const p10 = px + oy1;
            if (w14)
              wdec14(buffer2[px + j], buffer2[p10 + j]);
            else
              wdec16(buffer2[px + j], buffer2[p10 + j]);
            i00 = wdec14Return.a;
            buffer2[p10 + j] = wdec14Return.b;
            buffer2[px + j] = i00;
          }
        }
        if (ny & p) {
          let px = py;
          const ex = py + ox * (nx - p2);
          for (; px <= ex; px += ox2) {
            const p01 = px + ox1;
            if (w14)
              wdec14(buffer2[px + j], buffer2[p01 + j]);
            else
              wdec16(buffer2[px + j], buffer2[p01 + j]);
            i00 = wdec14Return.a;
            buffer2[p01 + j] = wdec14Return.b;
            buffer2[px + j] = i00;
          }
        }
        p2 = p;
        p >>= 1;
      }
      return py;
    }
    function hufDecode(encodingTable, decodingTable, uInt8Array2, inOffset, ni, rlc, no, outBuffer, outOffset) {
      let c = 0;
      let lc = 0;
      const outBufferEndOffset = no;
      const inOffsetEnd = Math.trunc(inOffset.value + (ni + 7) / 8);
      while (inOffset.value < inOffsetEnd) {
        getChar(c, lc, uInt8Array2, inOffset);
        c = getCharReturn.c;
        lc = getCharReturn.lc;
        while (lc >= HUF_DECBITS) {
          const index = c >> lc - HUF_DECBITS & HUF_DECMASK;
          const pl = decodingTable[index];
          if (pl.len) {
            lc -= pl.len;
            getCode(pl.lit, rlc, c, lc, uInt8Array2, inOffset, outBuffer, outOffset, outBufferEndOffset);
            c = getCodeReturn.c;
            lc = getCodeReturn.lc;
          } else {
            if (!pl.p) {
              throw new Error("hufDecode issues");
            }
            let j;
            for (j = 0; j < pl.lit; j++) {
              const l = hufLength(encodingTable[pl.p[j]]);
              while (lc < l && inOffset.value < inOffsetEnd) {
                getChar(c, lc, uInt8Array2, inOffset);
                c = getCharReturn.c;
                lc = getCharReturn.lc;
              }
              if (lc >= l) {
                if (hufCode(encodingTable[pl.p[j]]) == (c >> lc - l & (1 << l) - 1)) {
                  lc -= l;
                  getCode(pl.p[j], rlc, c, lc, uInt8Array2, inOffset, outBuffer, outOffset, outBufferEndOffset);
                  c = getCodeReturn.c;
                  lc = getCodeReturn.lc;
                  break;
                }
              }
            }
            if (j == pl.lit) {
              throw new Error("hufDecode issues");
            }
          }
        }
      }
      const i = 8 - ni & 7;
      c >>= i;
      lc -= i;
      while (lc > 0) {
        const pl = decodingTable[c << HUF_DECBITS - lc & HUF_DECMASK];
        if (pl.len) {
          lc -= pl.len;
          getCode(pl.lit, rlc, c, lc, uInt8Array2, inOffset, outBuffer, outOffset, outBufferEndOffset);
          c = getCodeReturn.c;
          lc = getCodeReturn.lc;
        } else {
          throw new Error("hufDecode issues");
        }
      }
      return true;
    }
    function hufUncompress(uInt8Array2, inDataView, inOffset, nCompressed, outBuffer, nRaw) {
      const outOffset = { value: 0 };
      const initialInOffset = inOffset.value;
      const im = parseUint32(inDataView, inOffset);
      const iM = parseUint32(inDataView, inOffset);
      inOffset.value += 4;
      const nBits = parseUint32(inDataView, inOffset);
      inOffset.value += 4;
      if (im < 0 || im >= HUF_ENCSIZE || iM < 0 || iM >= HUF_ENCSIZE) {
        throw new Error("Something wrong with HUF_ENCSIZE");
      }
      const freq = new Array(HUF_ENCSIZE);
      const hdec = new Array(HUF_DECSIZE);
      hufClearDecTable(hdec);
      const ni = nCompressed - (inOffset.value - initialInOffset);
      hufUnpackEncTable(uInt8Array2, inOffset, ni, im, iM, freq);
      if (nBits > 8 * (nCompressed - (inOffset.value - initialInOffset))) {
        throw new Error("Something wrong with hufUncompress");
      }
      hufBuildDecTable(freq, im, iM, hdec);
      hufDecode(freq, hdec, uInt8Array2, inOffset, nBits, iM, nRaw, outBuffer, outOffset);
    }
    function applyLut(lut, data, nData) {
      for (let i = 0; i < nData; ++i) {
        data[i] = lut[data[i]];
      }
    }
    function predictor(source) {
      for (let t = 1; t < source.length; t++) {
        const d = source[t - 1] + source[t] - 128;
        source[t] = d;
      }
    }
    function interleaveScalar(source, out) {
      let t1 = 0;
      let t2 = Math.floor((source.length + 1) / 2);
      let s = 0;
      const stop = source.length - 1;
      while (true) {
        if (s > stop)
          break;
        out[s++] = source[t1++];
        if (s > stop)
          break;
        out[s++] = source[t2++];
      }
    }
    function decodeRunLength(source) {
      let size = source.byteLength;
      const out = new Array();
      let p = 0;
      const reader = new DataView(source);
      while (size > 0) {
        const l = reader.getInt8(p++);
        if (l < 0) {
          const count = -l;
          size -= count + 1;
          for (let i = 0; i < count; i++) {
            out.push(reader.getUint8(p++));
          }
        } else {
          const count = l;
          size -= 2;
          const value = reader.getUint8(p++);
          for (let i = 0; i < count + 1; i++) {
            out.push(value);
          }
        }
      }
      return out;
    }
    function lossyDctDecode(cscSet, rowPtrs, channelData, acBuffer, dcBuffer, outBuffer) {
      let dataView = new DataView(outBuffer.buffer);
      const width = channelData[cscSet.idx[0]].width;
      const height = channelData[cscSet.idx[0]].height;
      const numComp = 3;
      const numFullBlocksX = Math.floor(width / 8);
      const numBlocksX = Math.ceil(width / 8);
      const numBlocksY = Math.ceil(height / 8);
      const leftoverX = width - (numBlocksX - 1) * 8;
      const leftoverY = height - (numBlocksY - 1) * 8;
      const currAcComp = { value: 0 };
      const currDcComp = new Array(numComp);
      const dctData = new Array(numComp);
      const halfZigBlock = new Array(numComp);
      const rowBlock = new Array(numComp);
      const rowOffsets = new Array(numComp);
      for (let comp = 0; comp < numComp; ++comp) {
        rowOffsets[comp] = rowPtrs[cscSet.idx[comp]];
        currDcComp[comp] = comp < 1 ? 0 : currDcComp[comp - 1] + numBlocksX * numBlocksY;
        dctData[comp] = new Float32Array(64);
        halfZigBlock[comp] = new Uint16Array(64);
        rowBlock[comp] = new Uint16Array(numBlocksX * 64);
      }
      for (let blocky = 0; blocky < numBlocksY; ++blocky) {
        let maxY = 8;
        if (blocky == numBlocksY - 1)
          maxY = leftoverY;
        let maxX = 8;
        for (let blockx = 0; blockx < numBlocksX; ++blockx) {
          if (blockx == numBlocksX - 1)
            maxX = leftoverX;
          for (let comp = 0; comp < numComp; ++comp) {
            halfZigBlock[comp].fill(0);
            halfZigBlock[comp][0] = dcBuffer[currDcComp[comp]++];
            unRleAC(currAcComp, acBuffer, halfZigBlock[comp]);
            unZigZag(halfZigBlock[comp], dctData[comp]);
            dctInverse(dctData[comp]);
          }
          if (numComp == 3) {
            csc709Inverse(dctData);
          }
          for (let comp = 0; comp < numComp; ++comp) {
            convertToHalf(dctData[comp], rowBlock[comp], blockx * 64);
          }
        }
        let offset2 = 0;
        for (let comp = 0; comp < numComp; ++comp) {
          const type = channelData[cscSet.idx[comp]].type;
          for (let y = 8 * blocky; y < 8 * blocky + maxY; ++y) {
            offset2 = rowOffsets[comp][y];
            for (let blockx = 0; blockx < numFullBlocksX; ++blockx) {
              const src = blockx * 64 + (y & 7) * 8;
              dataView.setUint16(offset2 + 0 * INT16_SIZE * type, rowBlock[comp][src + 0], true);
              dataView.setUint16(offset2 + 1 * INT16_SIZE * type, rowBlock[comp][src + 1], true);
              dataView.setUint16(offset2 + 2 * INT16_SIZE * type, rowBlock[comp][src + 2], true);
              dataView.setUint16(offset2 + 3 * INT16_SIZE * type, rowBlock[comp][src + 3], true);
              dataView.setUint16(offset2 + 4 * INT16_SIZE * type, rowBlock[comp][src + 4], true);
              dataView.setUint16(offset2 + 5 * INT16_SIZE * type, rowBlock[comp][src + 5], true);
              dataView.setUint16(offset2 + 6 * INT16_SIZE * type, rowBlock[comp][src + 6], true);
              dataView.setUint16(offset2 + 7 * INT16_SIZE * type, rowBlock[comp][src + 7], true);
              offset2 += 8 * INT16_SIZE * type;
            }
          }
          if (numFullBlocksX != numBlocksX) {
            for (let y = 8 * blocky; y < 8 * blocky + maxY; ++y) {
              const offset3 = rowOffsets[comp][y] + 8 * numFullBlocksX * INT16_SIZE * type;
              const src = numFullBlocksX * 64 + (y & 7) * 8;
              for (let x = 0; x < maxX; ++x) {
                dataView.setUint16(offset3 + x * INT16_SIZE * type, rowBlock[comp][src + x], true);
              }
            }
          }
        }
      }
      const halfRow = new Uint16Array(width);
      dataView = new DataView(outBuffer.buffer);
      for (let comp = 0; comp < numComp; ++comp) {
        channelData[cscSet.idx[comp]].decoded = true;
        const type = channelData[cscSet.idx[comp]].type;
        if (channelData[comp].type != 2)
          continue;
        for (let y = 0; y < height; ++y) {
          const offset2 = rowOffsets[comp][y];
          for (let x = 0; x < width; ++x) {
            halfRow[x] = dataView.getUint16(offset2 + x * INT16_SIZE * type, true);
          }
          for (let x = 0; x < width; ++x) {
            dataView.setFloat32(offset2 + x * INT16_SIZE * type, decodeFloat16(halfRow[x]), true);
          }
        }
      }
    }
    function unRleAC(currAcComp, acBuffer, halfZigBlock) {
      let acValue;
      let dctComp = 1;
      while (dctComp < 64) {
        acValue = acBuffer[currAcComp.value];
        if (acValue == 65280) {
          dctComp = 64;
        } else if (acValue >> 8 == 255) {
          dctComp += acValue & 255;
        } else {
          halfZigBlock[dctComp] = acValue;
          dctComp++;
        }
        currAcComp.value++;
      }
    }
    function unZigZag(src, dst) {
      dst[0] = decodeFloat16(src[0]);
      dst[1] = decodeFloat16(src[1]);
      dst[2] = decodeFloat16(src[5]);
      dst[3] = decodeFloat16(src[6]);
      dst[4] = decodeFloat16(src[14]);
      dst[5] = decodeFloat16(src[15]);
      dst[6] = decodeFloat16(src[27]);
      dst[7] = decodeFloat16(src[28]);
      dst[8] = decodeFloat16(src[2]);
      dst[9] = decodeFloat16(src[4]);
      dst[10] = decodeFloat16(src[7]);
      dst[11] = decodeFloat16(src[13]);
      dst[12] = decodeFloat16(src[16]);
      dst[13] = decodeFloat16(src[26]);
      dst[14] = decodeFloat16(src[29]);
      dst[15] = decodeFloat16(src[42]);
      dst[16] = decodeFloat16(src[3]);
      dst[17] = decodeFloat16(src[8]);
      dst[18] = decodeFloat16(src[12]);
      dst[19] = decodeFloat16(src[17]);
      dst[20] = decodeFloat16(src[25]);
      dst[21] = decodeFloat16(src[30]);
      dst[22] = decodeFloat16(src[41]);
      dst[23] = decodeFloat16(src[43]);
      dst[24] = decodeFloat16(src[9]);
      dst[25] = decodeFloat16(src[11]);
      dst[26] = decodeFloat16(src[18]);
      dst[27] = decodeFloat16(src[24]);
      dst[28] = decodeFloat16(src[31]);
      dst[29] = decodeFloat16(src[40]);
      dst[30] = decodeFloat16(src[44]);
      dst[31] = decodeFloat16(src[53]);
      dst[32] = decodeFloat16(src[10]);
      dst[33] = decodeFloat16(src[19]);
      dst[34] = decodeFloat16(src[23]);
      dst[35] = decodeFloat16(src[32]);
      dst[36] = decodeFloat16(src[39]);
      dst[37] = decodeFloat16(src[45]);
      dst[38] = decodeFloat16(src[52]);
      dst[39] = decodeFloat16(src[54]);
      dst[40] = decodeFloat16(src[20]);
      dst[41] = decodeFloat16(src[22]);
      dst[42] = decodeFloat16(src[33]);
      dst[43] = decodeFloat16(src[38]);
      dst[44] = decodeFloat16(src[46]);
      dst[45] = decodeFloat16(src[51]);
      dst[46] = decodeFloat16(src[55]);
      dst[47] = decodeFloat16(src[60]);
      dst[48] = decodeFloat16(src[21]);
      dst[49] = decodeFloat16(src[34]);
      dst[50] = decodeFloat16(src[37]);
      dst[51] = decodeFloat16(src[47]);
      dst[52] = decodeFloat16(src[50]);
      dst[53] = decodeFloat16(src[56]);
      dst[54] = decodeFloat16(src[59]);
      dst[55] = decodeFloat16(src[61]);
      dst[56] = decodeFloat16(src[35]);
      dst[57] = decodeFloat16(src[36]);
      dst[58] = decodeFloat16(src[48]);
      dst[59] = decodeFloat16(src[49]);
      dst[60] = decodeFloat16(src[57]);
      dst[61] = decodeFloat16(src[58]);
      dst[62] = decodeFloat16(src[62]);
      dst[63] = decodeFloat16(src[63]);
    }
    function dctInverse(data) {
      const a = 0.5 * Math.cos(3.14159 / 4);
      const b = 0.5 * Math.cos(3.14159 / 16);
      const c = 0.5 * Math.cos(3.14159 / 8);
      const d = 0.5 * Math.cos(3 * 3.14159 / 16);
      const e = 0.5 * Math.cos(5 * 3.14159 / 16);
      const f = 0.5 * Math.cos(3 * 3.14159 / 8);
      const g = 0.5 * Math.cos(7 * 3.14159 / 16);
      const alpha = new Array(4);
      const beta = new Array(4);
      const theta = new Array(4);
      const gamma = new Array(4);
      for (let row = 0; row < 8; ++row) {
        const rowPtr = row * 8;
        alpha[0] = c * data[rowPtr + 2];
        alpha[1] = f * data[rowPtr + 2];
        alpha[2] = c * data[rowPtr + 6];
        alpha[3] = f * data[rowPtr + 6];
        beta[0] = b * data[rowPtr + 1] + d * data[rowPtr + 3] + e * data[rowPtr + 5] + g * data[rowPtr + 7];
        beta[1] = d * data[rowPtr + 1] - g * data[rowPtr + 3] - b * data[rowPtr + 5] - e * data[rowPtr + 7];
        beta[2] = e * data[rowPtr + 1] - b * data[rowPtr + 3] + g * data[rowPtr + 5] + d * data[rowPtr + 7];
        beta[3] = g * data[rowPtr + 1] - e * data[rowPtr + 3] + d * data[rowPtr + 5] - b * data[rowPtr + 7];
        theta[0] = a * (data[rowPtr + 0] + data[rowPtr + 4]);
        theta[3] = a * (data[rowPtr + 0] - data[rowPtr + 4]);
        theta[1] = alpha[0] + alpha[3];
        theta[2] = alpha[1] - alpha[2];
        gamma[0] = theta[0] + theta[1];
        gamma[1] = theta[3] + theta[2];
        gamma[2] = theta[3] - theta[2];
        gamma[3] = theta[0] - theta[1];
        data[rowPtr + 0] = gamma[0] + beta[0];
        data[rowPtr + 1] = gamma[1] + beta[1];
        data[rowPtr + 2] = gamma[2] + beta[2];
        data[rowPtr + 3] = gamma[3] + beta[3];
        data[rowPtr + 4] = gamma[3] - beta[3];
        data[rowPtr + 5] = gamma[2] - beta[2];
        data[rowPtr + 6] = gamma[1] - beta[1];
        data[rowPtr + 7] = gamma[0] - beta[0];
      }
      for (let column = 0; column < 8; ++column) {
        alpha[0] = c * data[16 + column];
        alpha[1] = f * data[16 + column];
        alpha[2] = c * data[48 + column];
        alpha[3] = f * data[48 + column];
        beta[0] = b * data[8 + column] + d * data[24 + column] + e * data[40 + column] + g * data[56 + column];
        beta[1] = d * data[8 + column] - g * data[24 + column] - b * data[40 + column] - e * data[56 + column];
        beta[2] = e * data[8 + column] - b * data[24 + column] + g * data[40 + column] + d * data[56 + column];
        beta[3] = g * data[8 + column] - e * data[24 + column] + d * data[40 + column] - b * data[56 + column];
        theta[0] = a * (data[column] + data[32 + column]);
        theta[3] = a * (data[column] - data[32 + column]);
        theta[1] = alpha[0] + alpha[3];
        theta[2] = alpha[1] - alpha[2];
        gamma[0] = theta[0] + theta[1];
        gamma[1] = theta[3] + theta[2];
        gamma[2] = theta[3] - theta[2];
        gamma[3] = theta[0] - theta[1];
        data[0 + column] = gamma[0] + beta[0];
        data[8 + column] = gamma[1] + beta[1];
        data[16 + column] = gamma[2] + beta[2];
        data[24 + column] = gamma[3] + beta[3];
        data[32 + column] = gamma[3] - beta[3];
        data[40 + column] = gamma[2] - beta[2];
        data[48 + column] = gamma[1] - beta[1];
        data[56 + column] = gamma[0] - beta[0];
      }
    }
    function csc709Inverse(data) {
      for (let i = 0; i < 64; ++i) {
        const y = data[0][i];
        const cb = data[1][i];
        const cr = data[2][i];
        data[0][i] = y + 1.5747 * cr;
        data[1][i] = y - 0.1873 * cb - 0.4682 * cr;
        data[2][i] = y + 1.8556 * cb;
      }
    }
    function convertToHalf(src, dst, idx) {
      for (let i = 0; i < 64; ++i) {
        dst[idx + i] = DataUtils.toHalfFloat(toLinear(src[i]));
      }
    }
    function toLinear(float) {
      if (float <= 1) {
        return Math.sign(float) * Math.pow(Math.abs(float), 2.2);
      } else {
        return Math.sign(float) * Math.pow(logBase, Math.abs(float) - 1);
      }
    }
    function uncompressRAW(info) {
      return new DataView(info.array.buffer, info.offset.value, info.size);
    }
    function uncompressRLE(info) {
      const compressed = info.viewer.buffer.slice(info.offset.value, info.offset.value + info.size);
      const rawBuffer = new Uint8Array(decodeRunLength(compressed));
      const tmpBuffer = new Uint8Array(rawBuffer.length);
      predictor(rawBuffer);
      interleaveScalar(rawBuffer, tmpBuffer);
      return new DataView(tmpBuffer.buffer);
    }
    function uncompressZIP(info) {
      const compressed = info.array.slice(info.offset.value, info.offset.value + info.size);
      const rawBuffer = unzlibSync(compressed);
      const tmpBuffer = new Uint8Array(rawBuffer.length);
      predictor(rawBuffer);
      interleaveScalar(rawBuffer, tmpBuffer);
      return new DataView(tmpBuffer.buffer);
    }
    function uncompressPIZ(info) {
      const inDataView = info.viewer;
      const inOffset = { value: info.offset.value };
      const outBuffer = new Uint16Array(info.width * info.scanlineBlockSize * (info.channels * info.type));
      const bitmap = new Uint8Array(BITMAP_SIZE);
      let outBufferEnd = 0;
      const pizChannelData = new Array(info.channels);
      for (let i = 0; i < info.channels; i++) {
        pizChannelData[i] = {};
        pizChannelData[i]["start"] = outBufferEnd;
        pizChannelData[i]["end"] = pizChannelData[i]["start"];
        pizChannelData[i]["nx"] = info.width;
        pizChannelData[i]["ny"] = info.lines;
        pizChannelData[i]["size"] = info.type;
        outBufferEnd += pizChannelData[i].nx * pizChannelData[i].ny * pizChannelData[i].size;
      }
      const minNonZero = parseUint16(inDataView, inOffset);
      const maxNonZero = parseUint16(inDataView, inOffset);
      if (maxNonZero >= BITMAP_SIZE) {
        throw new Error("Something is wrong with PIZ_COMPRESSION BITMAP_SIZE");
      }
      if (minNonZero <= maxNonZero) {
        for (let i = 0; i < maxNonZero - minNonZero + 1; i++) {
          bitmap[i + minNonZero] = parseUint8(inDataView, inOffset);
        }
      }
      const lut = new Uint16Array(USHORT_RANGE);
      const maxValue = reverseLutFromBitmap(bitmap, lut);
      const length = parseUint32(inDataView, inOffset);
      hufUncompress(info.array, inDataView, inOffset, length, outBuffer, outBufferEnd);
      for (let i = 0; i < info.channels; ++i) {
        const cd = pizChannelData[i];
        for (let j = 0; j < pizChannelData[i].size; ++j) {
          wav2Decode(
            outBuffer,
            cd.start + j,
            cd.nx,
            cd.size,
            cd.ny,
            cd.nx * cd.size,
            maxValue
          );
        }
      }
      applyLut(lut, outBuffer, outBufferEnd);
      let tmpOffset2 = 0;
      const tmpBuffer = new Uint8Array(outBuffer.buffer.byteLength);
      for (let y = 0; y < info.lines; y++) {
        for (let c = 0; c < info.channels; c++) {
          const cd = pizChannelData[c];
          const n = cd.nx * cd.size;
          const cp = new Uint8Array(outBuffer.buffer, cd.end * INT16_SIZE, n * INT16_SIZE);
          tmpBuffer.set(cp, tmpOffset2);
          tmpOffset2 += n * INT16_SIZE;
          cd.end += n;
        }
      }
      return new DataView(tmpBuffer.buffer);
    }
    function uncompressPXR(info) {
      const compressed = info.array.slice(info.offset.value, info.offset.value + info.size);
      const rawBuffer = unzlibSync(compressed);
      const sz = info.lines * info.channels * info.width;
      const tmpBuffer = info.type == 1 ? new Uint16Array(sz) : new Uint32Array(sz);
      let tmpBufferEnd = 0;
      let writePtr = 0;
      const ptr = new Array(4);
      for (let y = 0; y < info.lines; y++) {
        for (let c = 0; c < info.channels; c++) {
          let pixel = 0;
          switch (info.type) {
            case 1:
              ptr[0] = tmpBufferEnd;
              ptr[1] = ptr[0] + info.width;
              tmpBufferEnd = ptr[1] + info.width;
              for (let j = 0; j < info.width; ++j) {
                const diff = rawBuffer[ptr[0]++] << 8 | rawBuffer[ptr[1]++];
                pixel += diff;
                tmpBuffer[writePtr] = pixel;
                writePtr++;
              }
              break;
            case 2:
              ptr[0] = tmpBufferEnd;
              ptr[1] = ptr[0] + info.width;
              ptr[2] = ptr[1] + info.width;
              tmpBufferEnd = ptr[2] + info.width;
              for (let j = 0; j < info.width; ++j) {
                const diff = rawBuffer[ptr[0]++] << 24 | rawBuffer[ptr[1]++] << 16 | rawBuffer[ptr[2]++] << 8;
                pixel += diff;
                tmpBuffer[writePtr] = pixel;
                writePtr++;
              }
              break;
          }
        }
      }
      return new DataView(tmpBuffer.buffer);
    }
    function uncompressDWA(info) {
      const inDataView = info.viewer;
      const inOffset = { value: info.offset.value };
      const outBuffer = new Uint8Array(info.width * info.lines * (info.channels * info.type * INT16_SIZE));
      const dwaHeader = {
        version: parseInt64(inDataView, inOffset),
        unknownUncompressedSize: parseInt64(inDataView, inOffset),
        unknownCompressedSize: parseInt64(inDataView, inOffset),
        acCompressedSize: parseInt64(inDataView, inOffset),
        dcCompressedSize: parseInt64(inDataView, inOffset),
        rleCompressedSize: parseInt64(inDataView, inOffset),
        rleUncompressedSize: parseInt64(inDataView, inOffset),
        rleRawSize: parseInt64(inDataView, inOffset),
        totalAcUncompressedCount: parseInt64(inDataView, inOffset),
        totalDcUncompressedCount: parseInt64(inDataView, inOffset),
        acCompression: parseInt64(inDataView, inOffset)
      };
      if (dwaHeader.version < 2)
        throw new Error("EXRLoader.parse: " + EXRHeader.compression + " version " + dwaHeader.version + " is unsupported");
      const channelRules = new Array();
      let ruleSize = parseUint16(inDataView, inOffset) - INT16_SIZE;
      while (ruleSize > 0) {
        const name = parseNullTerminatedString(inDataView.buffer, inOffset);
        const value = parseUint8(inDataView, inOffset);
        const compression = value >> 2 & 3;
        const csc = (value >> 4) - 1;
        const index = new Int8Array([csc])[0];
        const type = parseUint8(inDataView, inOffset);
        channelRules.push({
          name,
          index,
          type,
          compression
        });
        ruleSize -= name.length + 3;
      }
      const channels = EXRHeader.channels;
      const channelData = new Array(info.channels);
      for (let i = 0; i < info.channels; ++i) {
        const cd = channelData[i] = {};
        const channel = channels[i];
        cd.name = channel.name;
        cd.compression = UNKNOWN;
        cd.decoded = false;
        cd.type = channel.pixelType;
        cd.pLinear = channel.pLinear;
        cd.width = info.width;
        cd.height = info.lines;
      }
      const cscSet = {
        idx: new Array(3)
      };
      for (let offset2 = 0; offset2 < info.channels; ++offset2) {
        const cd = channelData[offset2];
        for (let i = 0; i < channelRules.length; ++i) {
          const rule = channelRules[i];
          if (cd.name == rule.name) {
            cd.compression = rule.compression;
            if (rule.index >= 0) {
              cscSet.idx[rule.index] = offset2;
            }
            cd.offset = offset2;
          }
        }
      }
      let acBuffer, dcBuffer, rleBuffer;
      if (dwaHeader.acCompressedSize > 0) {
        switch (dwaHeader.acCompression) {
          case STATIC_HUFFMAN:
            acBuffer = new Uint16Array(dwaHeader.totalAcUncompressedCount);
            hufUncompress(info.array, inDataView, inOffset, dwaHeader.acCompressedSize, acBuffer, dwaHeader.totalAcUncompressedCount);
            break;
          case DEFLATE:
            const compressed = info.array.slice(inOffset.value, inOffset.value + dwaHeader.totalAcUncompressedCount);
            const data = unzlibSync(compressed);
            acBuffer = new Uint16Array(data.buffer);
            inOffset.value += dwaHeader.totalAcUncompressedCount;
            break;
        }
      }
      if (dwaHeader.dcCompressedSize > 0) {
        const zlibInfo = {
          array: info.array,
          offset: inOffset,
          size: dwaHeader.dcCompressedSize
        };
        dcBuffer = new Uint16Array(uncompressZIP(zlibInfo).buffer);
        inOffset.value += dwaHeader.dcCompressedSize;
      }
      if (dwaHeader.rleRawSize > 0) {
        const compressed = info.array.slice(inOffset.value, inOffset.value + dwaHeader.rleCompressedSize);
        const data = unzlibSync(compressed);
        rleBuffer = decodeRunLength(data.buffer);
        inOffset.value += dwaHeader.rleCompressedSize;
      }
      let outBufferEnd = 0;
      const rowOffsets = new Array(channelData.length);
      for (let i = 0; i < rowOffsets.length; ++i) {
        rowOffsets[i] = new Array();
      }
      for (let y = 0; y < info.lines; ++y) {
        for (let chan = 0; chan < channelData.length; ++chan) {
          rowOffsets[chan].push(outBufferEnd);
          outBufferEnd += channelData[chan].width * info.type * INT16_SIZE;
        }
      }
      lossyDctDecode(cscSet, rowOffsets, channelData, acBuffer, dcBuffer, outBuffer);
      for (let i = 0; i < channelData.length; ++i) {
        const cd = channelData[i];
        if (cd.decoded)
          continue;
        switch (cd.compression) {
          case RLE:
            let row = 0;
            let rleOffset = 0;
            for (let y = 0; y < info.lines; ++y) {
              let rowOffsetBytes = rowOffsets[i][row];
              for (let x = 0; x < cd.width; ++x) {
                for (let byte = 0; byte < INT16_SIZE * cd.type; ++byte) {
                  outBuffer[rowOffsetBytes++] = rleBuffer[rleOffset + byte * cd.width * cd.height];
                }
                rleOffset++;
              }
              row++;
            }
            break;
          case LOSSY_DCT:
          default:
            throw new Error("EXRLoader.parse: unsupported channel compression");
        }
      }
      return new DataView(outBuffer.buffer);
    }
    function parseNullTerminatedString(buffer2, offset2) {
      const uintBuffer = new Uint8Array(buffer2);
      let endOffset = 0;
      while (uintBuffer[offset2.value + endOffset] != 0) {
        endOffset += 1;
      }
      const stringValue = new TextDecoder().decode(
        uintBuffer.slice(offset2.value, offset2.value + endOffset)
      );
      offset2.value = offset2.value + endOffset + 1;
      return stringValue;
    }
    function parseFixedLengthString(buffer2, offset2, size) {
      const stringValue = new TextDecoder().decode(
        new Uint8Array(buffer2).slice(offset2.value, offset2.value + size)
      );
      offset2.value = offset2.value + size;
      return stringValue;
    }
    function parseRational(dataView, offset2) {
      const x = parseInt32(dataView, offset2);
      const y = parseUint32(dataView, offset2);
      return [x, y];
    }
    function parseTimecode(dataView, offset2) {
      const x = parseUint32(dataView, offset2);
      const y = parseUint32(dataView, offset2);
      return [x, y];
    }
    function parseInt32(dataView, offset2) {
      const Int32 = dataView.getInt32(offset2.value, true);
      offset2.value = offset2.value + INT32_SIZE;
      return Int32;
    }
    function parseUint32(dataView, offset2) {
      const Uint32 = dataView.getUint32(offset2.value, true);
      offset2.value = offset2.value + INT32_SIZE;
      return Uint32;
    }
    function parseUint8Array(uInt8Array2, offset2) {
      const Uint8 = uInt8Array2[offset2.value];
      offset2.value = offset2.value + INT8_SIZE;
      return Uint8;
    }
    function parseUint8(dataView, offset2) {
      const Uint8 = dataView.getUint8(offset2.value);
      offset2.value = offset2.value + INT8_SIZE;
      return Uint8;
    }
    const parseInt64 = function(dataView, offset2) {
      let int;
      if ("getBigInt64" in DataView.prototype) {
        int = Number(dataView.getBigInt64(offset2.value, true));
      } else {
        int = dataView.getUint32(offset2.value + 4, true) + Number(dataView.getUint32(offset2.value, true) << 32);
      }
      offset2.value += ULONG_SIZE;
      return int;
    };
    function parseFloat32(dataView, offset2) {
      const float = dataView.getFloat32(offset2.value, true);
      offset2.value += FLOAT32_SIZE;
      return float;
    }
    function decodeFloat32(dataView, offset2) {
      return DataUtils.toHalfFloat(parseFloat32(dataView, offset2));
    }
    function decodeFloat16(binary) {
      const exponent = (binary & 31744) >> 10, fraction = binary & 1023;
      return (binary >> 15 ? -1 : 1) * (exponent ? exponent === 31 ? fraction ? NaN : Infinity : Math.pow(2, exponent - 15) * (1 + fraction / 1024) : 6103515625e-14 * (fraction / 1024));
    }
    function parseUint16(dataView, offset2) {
      const Uint16 = dataView.getUint16(offset2.value, true);
      offset2.value += INT16_SIZE;
      return Uint16;
    }
    function parseFloat16(buffer2, offset2) {
      return decodeFloat16(parseUint16(buffer2, offset2));
    }
    function parseChlist(dataView, buffer2, offset2, size) {
      const startOffset = offset2.value;
      const channels = [];
      while (offset2.value < startOffset + size - 1) {
        const name = parseNullTerminatedString(buffer2, offset2);
        const pixelType = parseInt32(dataView, offset2);
        const pLinear = parseUint8(dataView, offset2);
        offset2.value += 3;
        const xSampling = parseInt32(dataView, offset2);
        const ySampling = parseInt32(dataView, offset2);
        channels.push({
          name,
          pixelType,
          pLinear,
          xSampling,
          ySampling
        });
      }
      offset2.value += 1;
      return channels;
    }
    function parseChromaticities(dataView, offset2) {
      const redX = parseFloat32(dataView, offset2);
      const redY = parseFloat32(dataView, offset2);
      const greenX = parseFloat32(dataView, offset2);
      const greenY = parseFloat32(dataView, offset2);
      const blueX = parseFloat32(dataView, offset2);
      const blueY = parseFloat32(dataView, offset2);
      const whiteX = parseFloat32(dataView, offset2);
      const whiteY = parseFloat32(dataView, offset2);
      return { redX, redY, greenX, greenY, blueX, blueY, whiteX, whiteY };
    }
    function parseCompression(dataView, offset2) {
      const compressionCodes = [
        "NO_COMPRESSION",
        "RLE_COMPRESSION",
        "ZIPS_COMPRESSION",
        "ZIP_COMPRESSION",
        "PIZ_COMPRESSION",
        "PXR24_COMPRESSION",
        "B44_COMPRESSION",
        "B44A_COMPRESSION",
        "DWAA_COMPRESSION",
        "DWAB_COMPRESSION"
      ];
      const compression = parseUint8(dataView, offset2);
      return compressionCodes[compression];
    }
    function parseBox2i(dataView, offset2) {
      const xMin = parseUint32(dataView, offset2);
      const yMin = parseUint32(dataView, offset2);
      const xMax = parseUint32(dataView, offset2);
      const yMax = parseUint32(dataView, offset2);
      return { xMin, yMin, xMax, yMax };
    }
    function parseLineOrder(dataView, offset2) {
      const lineOrders = [
        "INCREASING_Y"
      ];
      const lineOrder = parseUint8(dataView, offset2);
      return lineOrders[lineOrder];
    }
    function parseV2f(dataView, offset2) {
      const x = parseFloat32(dataView, offset2);
      const y = parseFloat32(dataView, offset2);
      return [x, y];
    }
    function parseV3f(dataView, offset2) {
      const x = parseFloat32(dataView, offset2);
      const y = parseFloat32(dataView, offset2);
      const z = parseFloat32(dataView, offset2);
      return [x, y, z];
    }
    function parseValue(dataView, buffer2, offset2, type, size) {
      if (type === "string" || type === "stringvector" || type === "iccProfile") {
        return parseFixedLengthString(buffer2, offset2, size);
      } else if (type === "chlist") {
        return parseChlist(dataView, buffer2, offset2, size);
      } else if (type === "chromaticities") {
        return parseChromaticities(dataView, offset2);
      } else if (type === "compression") {
        return parseCompression(dataView, offset2);
      } else if (type === "box2i") {
        return parseBox2i(dataView, offset2);
      } else if (type === "lineOrder") {
        return parseLineOrder(dataView, offset2);
      } else if (type === "float") {
        return parseFloat32(dataView, offset2);
      } else if (type === "v2f") {
        return parseV2f(dataView, offset2);
      } else if (type === "v3f") {
        return parseV3f(dataView, offset2);
      } else if (type === "int") {
        return parseInt32(dataView, offset2);
      } else if (type === "rational") {
        return parseRational(dataView, offset2);
      } else if (type === "timecode") {
        return parseTimecode(dataView, offset2);
      } else if (type === "preview") {
        offset2.value += size;
        return "skipped";
      } else {
        offset2.value += size;
        return void 0;
      }
    }
    function parseHeader(dataView, buffer2, offset2) {
      const EXRHeader2 = {};
      if (dataView.getUint32(0, true) != 20000630) {
        throw new Error("THREE.EXRLoader: provided file doesn't appear to be in OpenEXR format.");
      }
      EXRHeader2.version = dataView.getUint8(4);
      const spec = dataView.getUint8(5);
      EXRHeader2.spec = {
        singleTile: !!(spec & 2),
        longName: !!(spec & 4),
        deepFormat: !!(spec & 8),
        multiPart: !!(spec & 16)
      };
      offset2.value = 8;
      let keepReading = true;
      while (keepReading) {
        const attributeName = parseNullTerminatedString(buffer2, offset2);
        if (attributeName == 0) {
          keepReading = false;
        } else {
          const attributeType = parseNullTerminatedString(buffer2, offset2);
          const attributeSize = parseUint32(dataView, offset2);
          const attributeValue = parseValue(dataView, buffer2, offset2, attributeType, attributeSize);
          if (attributeValue === void 0) {
            console.warn(`EXRLoader.parse: skipped unknown header attribute type '${attributeType}'.`);
          } else {
            EXRHeader2[attributeName] = attributeValue;
          }
        }
      }
      if ((spec & ~4) != 0) {
        console.error("EXRHeader:", EXRHeader2);
        throw new Error("THREE.EXRLoader: provided file is currently unsupported.");
      }
      return EXRHeader2;
    }
    function setupDecoder(EXRHeader2, dataView, uInt8Array2, offset2, outputType) {
      const EXRDecoder2 = {
        size: 0,
        viewer: dataView,
        array: uInt8Array2,
        offset: offset2,
        width: EXRHeader2.dataWindow.xMax - EXRHeader2.dataWindow.xMin + 1,
        height: EXRHeader2.dataWindow.yMax - EXRHeader2.dataWindow.yMin + 1,
        channels: EXRHeader2.channels.length,
        bytesPerLine: null,
        lines: null,
        inputSize: null,
        type: EXRHeader2.channels[0].pixelType,
        uncompress: null,
        getter: null,
        format: null,
        encoding: null
      };
      switch (EXRHeader2.compression) {
        case "NO_COMPRESSION":
          EXRDecoder2.lines = 1;
          EXRDecoder2.uncompress = uncompressRAW;
          break;
        case "RLE_COMPRESSION":
          EXRDecoder2.lines = 1;
          EXRDecoder2.uncompress = uncompressRLE;
          break;
        case "ZIPS_COMPRESSION":
          EXRDecoder2.lines = 1;
          EXRDecoder2.uncompress = uncompressZIP;
          break;
        case "ZIP_COMPRESSION":
          EXRDecoder2.lines = 16;
          EXRDecoder2.uncompress = uncompressZIP;
          break;
        case "PIZ_COMPRESSION":
          EXRDecoder2.lines = 32;
          EXRDecoder2.uncompress = uncompressPIZ;
          break;
        case "PXR24_COMPRESSION":
          EXRDecoder2.lines = 16;
          EXRDecoder2.uncompress = uncompressPXR;
          break;
        case "DWAA_COMPRESSION":
          EXRDecoder2.lines = 32;
          EXRDecoder2.uncompress = uncompressDWA;
          break;
        case "DWAB_COMPRESSION":
          EXRDecoder2.lines = 256;
          EXRDecoder2.uncompress = uncompressDWA;
          break;
        default:
          throw new Error("EXRLoader.parse: " + EXRHeader2.compression + " is unsupported");
      }
      EXRDecoder2.scanlineBlockSize = EXRDecoder2.lines;
      if (EXRDecoder2.type == 1) {
        switch (outputType) {
          case FloatType:
            EXRDecoder2.getter = parseFloat16;
            EXRDecoder2.inputSize = INT16_SIZE;
            break;
          case HalfFloatType:
            EXRDecoder2.getter = parseUint16;
            EXRDecoder2.inputSize = INT16_SIZE;
            break;
        }
      } else if (EXRDecoder2.type == 2) {
        switch (outputType) {
          case FloatType:
            EXRDecoder2.getter = parseFloat32;
            EXRDecoder2.inputSize = FLOAT32_SIZE;
            break;
          case HalfFloatType:
            EXRDecoder2.getter = decodeFloat32;
            EXRDecoder2.inputSize = FLOAT32_SIZE;
        }
      } else {
        throw new Error("EXRLoader.parse: unsupported pixelType " + EXRDecoder2.type + " for " + EXRHeader2.compression + ".");
      }
      EXRDecoder2.blockCount = (EXRHeader2.dataWindow.yMax + 1) / EXRDecoder2.scanlineBlockSize;
      for (let i = 0; i < EXRDecoder2.blockCount; i++)
        parseInt64(dataView, offset2);
      EXRDecoder2.outputChannels = EXRDecoder2.channels == 3 ? 4 : EXRDecoder2.channels;
      const size = EXRDecoder2.width * EXRDecoder2.height * EXRDecoder2.outputChannels;
      switch (outputType) {
        case FloatType:
          EXRDecoder2.byteArray = new Float32Array(size);
          if (EXRDecoder2.channels < EXRDecoder2.outputChannels)
            EXRDecoder2.byteArray.fill(1, 0, size);
          break;
        case HalfFloatType:
          EXRDecoder2.byteArray = new Uint16Array(size);
          if (EXRDecoder2.channels < EXRDecoder2.outputChannels)
            EXRDecoder2.byteArray.fill(15360, 0, size);
          break;
        default:
          console.error("THREE.EXRLoader: unsupported type: ", outputType);
          break;
      }
      EXRDecoder2.bytesPerLine = EXRDecoder2.width * EXRDecoder2.inputSize * EXRDecoder2.channels;
      if (EXRDecoder2.outputChannels == 4) {
        EXRDecoder2.format = RGBAFormat;
        EXRDecoder2.encoding = LinearEncoding;
      } else {
        EXRDecoder2.format = RedFormat;
        EXRDecoder2.encoding = LinearEncoding;
      }
      return EXRDecoder2;
    }
    const bufferDataView = new DataView(buffer);
    const uInt8Array = new Uint8Array(buffer);
    const offset = { value: 0 };
    const EXRHeader = parseHeader(bufferDataView, buffer, offset);
    const EXRDecoder = setupDecoder(EXRHeader, bufferDataView, uInt8Array, offset, this.type);
    const tmpOffset = { value: 0 };
    const channelOffsets = { R: 0, G: 1, B: 2, A: 3, Y: 0 };
    for (let scanlineBlockIdx = 0; scanlineBlockIdx < EXRDecoder.height / EXRDecoder.scanlineBlockSize; scanlineBlockIdx++) {
      const line = parseUint32(bufferDataView, offset);
      EXRDecoder.size = parseUint32(bufferDataView, offset);
      EXRDecoder.lines = line + EXRDecoder.scanlineBlockSize > EXRDecoder.height ? EXRDecoder.height - line : EXRDecoder.scanlineBlockSize;
      const isCompressed = EXRDecoder.size < EXRDecoder.lines * EXRDecoder.bytesPerLine;
      const viewer = isCompressed ? EXRDecoder.uncompress(EXRDecoder) : uncompressRAW(EXRDecoder);
      offset.value += EXRDecoder.size;
      for (let line_y = 0; line_y < EXRDecoder.scanlineBlockSize; line_y++) {
        const true_y = line_y + scanlineBlockIdx * EXRDecoder.scanlineBlockSize;
        if (true_y >= EXRDecoder.height)
          break;
        for (let channelID = 0; channelID < EXRDecoder.channels; channelID++) {
          const cOff = channelOffsets[EXRHeader.channels[channelID].name];
          for (let x = 0; x < EXRDecoder.width; x++) {
            tmpOffset.value = (line_y * (EXRDecoder.channels * EXRDecoder.width) + channelID * EXRDecoder.width + x) * EXRDecoder.inputSize;
            const outIndex = (EXRDecoder.height - 1 - true_y) * (EXRDecoder.width * EXRDecoder.outputChannels) + x * EXRDecoder.outputChannels + cOff;
            EXRDecoder.byteArray[outIndex] = EXRDecoder.getter(viewer, tmpOffset);
          }
        }
      }
    }
    return {
      header: EXRHeader,
      width: EXRDecoder.width,
      height: EXRDecoder.height,
      data: EXRDecoder.byteArray,
      format: EXRDecoder.format,
      encoding: EXRDecoder.encoding,
      type: this.type
    };
  }
  setDataType(value) {
    this.type = value;
    return this;
  }
  load(url, onLoad, onProgress, onError) {
    function onLoadCallback(texture, texData) {
      texture.encoding = texData.encoding;
      texture.minFilter = LinearFilter;
      texture.magFilter = LinearFilter;
      texture.generateMipmaps = false;
      texture.flipY = false;
      if (onLoad)
        onLoad(texture, texData);
    }
    return super.load(url, onLoadCallback, onProgress, onError);
  }
};

// src/shaders/compositor.frag
var compositor_default = "struct Highlight {\n  float regionId;\n  float alpha;\n  vec3 color;\n};\n\nstruct Selection {\n  float regionId;\n  float strength;\n  vec3 color;\n};\n\nstruct Mark {\n  float regionId;\n  float alpha;\n  vec3 color;\n};\n\nstruct RegionMaterial {\n  sampler2D texture;\n  bool rotate;\n  vec3 tint;\n  float width;\n  float height;\n};\n\nstruct LightingAdjustment {\n  bool enabled;\n  float inputMin;\n  float inputMax;\n  float outputMin;\n  float outputMax;\n  float gammaCorrection;\n};\n\n//\n// -------------------- Uniforms -------------------- \n//\n\n// Fade-in transition\nuniform float alpha;\n\n// Material transition state\nuniform float transitionProgress;\n\n// Scene scale\nuniform float scale;\n\n// Color to flash in the texture application transition\nuniform vec3 flashColor;\n\n// Material swap region ID\nuniform float matRegionId;\n\nuniform Selection selection;\n\nfloat highlightStrength = 0.25;\nfloat markStrength = 0.5;\n\nuniform Highlight highlight;\n\n// NOTE: this length must match `PREVIOUS_HIGHLIGHT_BUFFER` on the JS side\nuniform Highlight previousHighlights[5];\n\n// NOTE: these lengths must match `MAXIMUM_MARKS` on the JS side\nuniform Mark marks[18];\nuniform Mark previousMarks[18];\n\n// Render passes\nuniform sampler2D beautyTex;\nuniform sampler2D uvTex;\nuniform sampler2D matteTex;\nuniform sampler2D lightTex;\n\nuniform LightingAdjustment lightingAdjustment;\n\nuniform RegionMaterial regionMaterial;\n\nvarying vec2 vUv;\n\n//\n// -------------------- Helpers -------------------- \n//\n\n\n/**\n * Apply levels adjustment similar to that in Photoshop or a photo-editing tool.\n */\nvec3 levelAdjustment(vec3 color, float inputMin, float inputMax, float outputMin, float outputMax) {\n  vec3 adjustedColor = clamp((color - vec3(inputMin)) / (inputMax - inputMin), vec3(0.0), vec3(1.0));\n\n  return mix(vec3(outputMin), vec3(outputMax), adjustedColor);\n}\n\n/**\n * Convert a color value to grayscale.\n */\nvec3 convertToGrayscale(vec3 color) {\n  // The coefficients hard-coded in this vector correspond to the perceived\n  // luminance for each channel based on human visual perception\n  float grayscale = dot(color, vec3(0.2126, 0.7152, 0.0722));\n\n  return vec3(grayscale);\n}\n\nvec3 gammaCorrection(vec3 color, float gamma) {\n    return vec3(pow(color.rgb, vec3(1.0 / gamma)));\n}\n\nfloat cryptomatte(float regionId, vec4 matte0Color) {\n  float w = 0.;\n  if (matte0Color.r == regionId) {\n    w += matte0Color.g;\n  }\n  if (matte0Color.b == regionId) {\n    w += matte0Color.a / 3.0;\n  }\n  return w;\n}\n\n/**\n * Get an outline around a given region\n *\n * See: https://io7m.com/documents/outline-glsl/\n */\nfloat getOutline(float regionId, vec2 uv) {\n  vec2 uvCenter = uv;\n\n  float dx = 0.003;\n  float dy = 0.003;\n\n  vec2 uvTop = vec2(uv.x, uv.y + dy);\n  vec2 uvRight = vec2(uv.x + dx, uv.y);\n  vec2 uvTopRight = vec2(uv.x + dx, uv.y + dy);\n\n  float mCenter   = cryptomatte(regionId, texture(matteTex, uvCenter));\n  float mTop      = cryptomatte(regionId, texture(matteTex, uvTop));\n  float mRight    = cryptomatte(regionId, texture(matteTex, uvRight));\n  float mTopRight = cryptomatte(regionId, texture(matteTex, uvTopRight));\n\n  float dT  = abs(mCenter - mTop);\n  float dR  = abs(mCenter - mRight);\n  float dTR = abs(mCenter - mTopRight);\n\n  float delta = 0.0;\n  delta = max(delta, dT);\n  delta = max(delta, dR);\n  delta = max(delta, dTR);\n\n  return delta;\n}\n\nbool isTextureLoaded(sampler2D tex) {\n  // Sample the texture at the center\n  return texture2D(tex, vec2(0.5)).a != 0.0; \n}\n\nvec4 colorRegion(\n  vec4 color,\n  float regionId,\n  float alpha,\n  vec3 regionColor,\n  float strength\n) {\n  vec4 matteColor = texture2D(matteTex, vUv);\n  float maskAlpha = cryptomatte(regionId, matteColor);\n\n  return mix(\n    color,\n    vec4(regionColor.xyz, 1),\n    maskAlpha * strength * float(alpha)\n  );\n}\n\n// Easing functions\n\nfloat easeOutExpo(float x) {\n  return x == 1.0 ? 1.0 : 1.0 - pow(2.0, -10.0 * x);\n}\n\nfloat easeOutCubic(float x) {\n  return 1.0 - pow(1.0 - x, 3.0);\n}\n\n/**\n * Apply the following color overlays to the scene:\n *  - Outline (used for selection)\n *  - Highlight (used for hover)\n *  - Marks (used to mark various regions an arbitrary color)\n */\nvec4 applyOverlays(vec4 color) {\n  if (selection.regionId != 0.) {\n    // Add outline color\n    float outline = getOutline(selection.regionId, vUv);\n    vec4 outlineColor = outline * vec4(selection.color, 1);\n\n    color += outlineColor;\n    color = mix(color, outlineColor, outline * selection.strength);\n  }\n\n  // Apply current region highlight, if set\n  color = colorRegion(\n    color,\n    highlight.regionId,\n    highlight.alpha,\n    highlight.color,\n    highlightStrength\n  );\n\n  // Fade out any previous region highlights\n  for (int i = 0; i < previousHighlights.length(); i++) {\n    Highlight previousHighlight = previousHighlights[i];\n    color = colorRegion(\n      color,\n      previousHighlight.regionId,\n      previousHighlight.alpha,\n      previousHighlight.color,\n      highlightStrength\n    );\n  }\n\n  // Apply region color marks\n  for (int i = 0; i < marks.length(); i++) {\n    Mark mark = marks[i];\n\n    color = colorRegion(\n      color,\n      mark.regionId,\n      mark.alpha,\n      mark.color,\n      markStrength\n    );\n  }\n\n  // Fade out previous region color marks\n  for (int i = 0; i < previousMarks.length(); i++) {\n    Mark mark = previousMarks[i];\n\n    color = colorRegion(\n      color,\n      mark.regionId,\n      mark.alpha,\n      mark.color,\n      markStrength\n    );\n  }\n\n  return color;\n}\n\n/**\n * Given a progress value between 0 and 1 return a value between 0 and 1 that\n * fades in to 1 at the specified peak progress value and back out to 0 as the\n * progress approaches 1\n */\nfloat fadeInOut(float progress, float peak) {\n  // Apply an easing function to the progress\n  float alpha;\n\n  if (progress < peak) {\n    alpha = easeOutExpo(progress / peak);\n  } else {\n    alpha = easeOutCubic((1.0 - progress) / (1.0 - peak));\n  }\n\n  return alpha;\n}\n\n//\n// -------------------- Main -------------------- \n//\n\nvoid main() {\n  // Beauty color\n  vec4 beautyColor = texture2D(beautyTex, vUv);\n\n  // Before the texture is loaded, make everything transparent\n  // (otherwise we end up rendering a black box)\n  if (!isTextureLoaded(beautyTex)) {\n    gl_FragColor = vec4(1, 1, 1, 0);\n    return;\n  }\n\n  // Only render the beauty pass & outline if the region is 0\n  if (matRegionId == 0.) {\n    vec4 finalColor = applyOverlays(beautyColor);\n\n    // Applies a fade effect when the material is first rendered\n    finalColor.a = alpha;\n\n    gl_FragColor = finalColor;\n    return;\n  }\n\n  // UV color\n  vec4 uvColor = texture2D(uvTex, vUv);\n\n  // Rotate the UV coordinates 90 degrees, if necessary\n  if (regionMaterial.rotate) {\n    float tmp = uvColor.r;\n    uvColor.r = uvColor.g;\n    uvColor.g = tmp;\n  }\n\n  // Material color\n  vec2 productScale = vec2(\n    uvColor.r * (1.0 / regionMaterial.width),\n    uvColor.g * (1.0 / regionMaterial.height)\n  );\n  vec4 matColor = texture2D(regionMaterial.texture, productScale.rg * scale);\n  \n  vec3 tint = lightingAdjustment.enabled\n    ? gammaCorrection(regionMaterial.tint.rgb, lightingAdjustment.gammaCorrection)\n    : regionMaterial.tint.rgb;\n\n  // Material tint\n  matColor *= vec4(tint.rgb, 1.0);\n\n  // Material matte\n  vec4 matteColor = texture2D(matteTex, vUv);\n  float maskAlpha = cryptomatte(matRegionId, matteColor);\n\n  // Add Lighting\n  vec4 lightColor = texture2D(lightTex, vUv);\n\n  if (lightingAdjustment.enabled) {\n    // Apply level adjustment to lighting texture (to make\n    // applied tint more true to the original RGB value)\n    lightColor = vec4(\n      levelAdjustment(\n        convertToGrayscale(lightColor.rgb),\n        lightingAdjustment.inputMin,\n        lightingAdjustment.inputMax,\n        lightingAdjustment.outputMin,\n        lightingAdjustment.outputMax\n      ),\n      lightColor.a\n    );\n  }\n\n  matColor *= lightColor;\n  \n  // Add transition color\n  vec4 currentColor = mix(beautyColor, matColor, maskAlpha);\n  vec4 flash = mix(beautyColor, vec4(flashColor.xyz, 1.0), maskAlpha * fadeInOut(transitionProgress, 0.25) * 0.5);\n  vec4 finalColor = mix(flash, currentColor, transitionProgress);\n\n  gl_FragColor = applyOverlays(finalColor);\n}";

// src/shaders/fullscreen.vert
var fullscreen_default = "varying vec2 vUv;\n\nvoid main() {\n  vUv = uv;\n  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);\n}";

// src/Compositor/ColorReader.ts
import * as THREE from "three";

// src/shaders/fullscreen.frag
var fullscreen_default2 = "varying vec2 vUv;\nuniform sampler2D tex;\n\nvoid main() {\n  gl_FragColor = texture2D(tex, vUv);\n}";

// src/Compositor/ColorReader.ts
var emptyPixel = () => {
  const pixel = new Float32Array(4);
  pixel[0] = -1;
  pixel[1] = -1;
  pixel[2] = -1;
  pixel[3] = -1;
  return pixel;
};
var ColorReader = class {
  constructor() {
    // Scene used to perform a fullscreen render
    this.scene = new THREE.Scene();
    // Renderer used for fullscreen render
    this.renderer = new THREE.WebGLRenderer();
    // Fullscreen render target
    this.renderTarget = new THREE.WebGLRenderTarget(
      1,
      1
    );
    // Fullscreen render material
    this.material = new THREE.ShaderMaterial();
    // Render camera
    this.camera = new THREE.OrthographicCamera();
    // Last texture resolution used
    this.lastResolution = new THREE.Vector2(-1, -1);
  }
  /** *
   * Initialize the object for a given texture resolution
   *
   * @param {THREE.Vector2} resolution - Render resolution. This should match the size of the texture.
   */
  initWithResolution(resolution) {
    this.lastResolution = resolution;
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(resolution.x, resolution.y);
    this.renderer.autoClear = false;
    this.renderTarget = new THREE.WebGLRenderTarget(
      resolution.x,
      resolution.y,
      {
        minFilter: THREE.NearestFilter,
        magFilter: THREE.NearestFilter,
        format: THREE.RGBAFormat,
        type: THREE.FloatType
      }
    );
    this.material = new THREE.ShaderMaterial({
      uniforms: { tex: { value: void 0 } },
      vertexShader: fullscreen_default,
      fragmentShader: fullscreen_default2,
      depthWrite: false
    });
    const plane = new THREE.PlaneGeometry(resolution.x, resolution.y);
    const quad = new THREE.Mesh(plane, this.material);
    quad.position.z = -100;
    this.scene.add(quad);
    this.camera = new THREE.OrthographicCamera(
      resolution.x / -2,
      resolution.x / 2,
      resolution.y / 2,
      resolution.y / -2,
      -1e4,
      1e4
    );
    this.camera.position.z = 100;
  }
  /** *
   * Retrieve a color from a texture at a specific pixel coordinate
   * @param {THREE.Vector2} ndc - The normalized device coordinate to read from the texture (centered at 0,0 with range [-1,1])
   * @param {THREE.Texture} tex - The texture to read from
   * @returns {Float32Array[4]} Color at the supplied UV coordinate OR -1,-1,-1 if no texture is available
   */
  getTextureColor(ndc, tex) {
    if (tex === void 0) {
      console.warn("ColorReader: no texture available");
      return emptyPixel();
    }
    if (!hasDimensions(tex)) {
      console.warn("ColorReader: couldn't get dimensions of texture");
      return emptyPixel();
    }
    const resolution = new THREE.Vector2(tex.image.width, tex.image.height);
    if (resolution.x !== this.lastResolution.x || resolution.y !== this.lastResolution.y) {
      this.initWithResolution(resolution);
    }
    const uv = new THREE.Vector2(
      (ndc.x / 2 + 0.5) * resolution.x,
      (ndc.y / 2 + 0.5) * resolution.y
    );
    uv.x = Math.round(uv.x);
    uv.y = Math.round(uv.y);
    this.material.uniforms.tex.value = tex;
    this.renderer.setRenderTarget(this.renderTarget);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    const pixel = new Float32Array(4);
    this.renderer.readRenderTargetPixels(
      this.renderTarget,
      uv.x,
      uv.y,
      1,
      1,
      pixel
    );
    return pixel;
  }
};

// src/Compositor/RegionTexture.ts
import * as THREE2 from "three";

// src/Compositor/sceneHelpers.ts
import { Color } from "three";
var PREVIOUS_HIGHLIGHT_BUFFER = 5;
var MAXIMUM_MARKS = 18;
var getEmptySelection = () => ({
  color: new Color(),
  regionId: 0,
  strength: 0
});
var getEmptyHighlight = () => ({
  alpha: 0,
  color: new Color(),
  regionId: 0
});
var filterUniform = (uniform) => uniform.value.filter(({ regionId }) => regionId !== 0);
var populateUniform = (empty, length, value) => Array.from({
  length
}).map((_, i) => value[i] ?? empty);
var populateHighlightsUniform = (value) => populateUniform(getEmptyHighlight(), PREVIOUS_HIGHLIGHT_BUFFER, value);
var populateMarksUniform = (value) => populateUniform(
  { regionId: 0, alpha: 0, color: new Color() },
  MAXIMUM_MARKS,
  value
);
var compareColoredRegions = (left, right) => left.regionId === right.regionId && left.color.equals(right.color);
var compareColoredRegion = (left) => (right) => compareColoredRegions(left, right);
var difference = (left, right, compare) => left.filter(
  (l) => !right.some((r) => compare ? compare(l, r) : l.regionId === r.regionId)
);
var deduplicate = (items, compare) => items.filter(
  (item, index) => index === items.findIndex(
    (innerItem) => compare ? compare(item, innerItem) : item.regionId === innerItem.regionId
  )
);
var inchesToMeters = (value) => value * 0.0254;
var textureResourceToUrl = (resource) => {
  if (typeof resource === "string")
    return [resource, () => {
    }];
  const url = URL.createObjectURL(new Blob([resource]));
  return [url, () => URL.revokeObjectURL(url)];
};
var getTextureImage = (texture) => texture.image instanceof HTMLImageElement ? texture.image : void 0;

// src/Compositor/RegionTexture.ts
var FailedTexture = class extends THREE2.Texture {
};
var createEmptyTexture = () => {
  const imageData = new ImageData(1, 1);
  imageData.data.set([255, 255, 255, 255]);
  const texture = new FailedTexture(imageData);
  texture.needsUpdate = true;
  return texture;
};
var RegionTexture = class {
  /** Constructor */
  constructor(regionId, url, width, height, rot90, tint, cache) {
    /**
     * The ID of the region to change
     */
    this.regionId = "";
    /**
     * The URL for the texture to use
     */
    this.url = "";
    /**
     * Should the texture be rotated 90 degrees
     */
    this.rotate = false;
    /**
     * Tint color
     */
    this.tint = new THREE2.Color(16777215);
    this.deferredTexture = null;
    this.regionId = regionId;
    this.url = url;
    this.width = width;
    this.height = height;
    this.rotate = rot90;
    this.tint = tint;
    this.cache = cache || {};
  }
  get texture() {
    const cached = this.cachedTexture;
    if (!cached) {
      console.warn("RegionTexture: accessed texture before it was loaded");
    }
    const texture = cached ?? this.deferredTexture ?? (this.deferredTexture = new THREE2.TextureLoader().load(
      this.url,
      this.onLoad.bind(this)
    ));
    texture.wrapS = THREE2.RepeatWrapping;
    texture.wrapT = THREE2.RepeatWrapping;
    return texture;
  }
  get cachedTexture() {
    return this.cache[this.url];
  }
  async load() {
    return new Promise((resolve, reject) => {
      const loader = new THREE2.TextureLoader();
      if (this.cachedTexture && !(this.cachedTexture instanceof FailedTexture)) {
        resolve(this);
        return;
      }
      this.deferredTexture = loader.load(
        this.url,
        (texture) => {
          this.onLoad(texture);
          resolve(this);
        },
        void 0,
        () => {
          this.onLoad(createEmptyTexture());
          reject(new Error(`RegionTexture: failed to load '${this.url}'`));
        }
      );
    });
  }
  asMaterial() {
    return {
      texture: this.texture,
      rotate: this.rotate,
      width: inchesToMeters(this.width),
      height: inchesToMeters(this.height),
      tint: this.tint
    };
  }
  onLoad(texture) {
    this.cache[this.url] = texture;
  }
};

// src/Compositor/SceneController.ts
var DEFAULT_COLOR = new THREE3.Color(0.1, 0.2, 0.9).getHex();
var DEFAULT_FLASH_COLOR = 0;
var DEFAULT_SCALE = 10;
var DEFAULT_ASPECT_RATIO = 16 / 9;
var DEFAULT_LIGHTING_ADJUSTMENT = {
  enabled: true,
  inputMin: 0,
  inputMax: 1,
  outputMin: 0,
  outputMax: 1,
  gammaCorrection: 1.2
};
var SceneController = class extends EventDispatcher {
  /**
   * Constructor
   *
   * @param domEl - DOM canvas element to render inside
   * @param resolution - Render resolution
   * @param resolution - Whether to have Three.js to apply dimensions to the
   * canvas element with CSS styles
   * @param preload - Preload necessary textures and cache from an existing
   * `SceneController`. This is used internally to redraw the scene at a
   * different resolution for the `download` method.
   */
  constructor(domEl, resolution, {
    updateStyles,
    animate = true,
    selectionColor = DEFAULT_COLOR,
    highlightColor = DEFAULT_COLOR,
    flashColor = DEFAULT_FLASH_COLOR,
    fadeSpeed = 4,
    transitionSpeed = 3,
    scale = DEFAULT_SCALE
  }) {
    super();
    // Main Scene
    this.scene = new THREE3.Scene();
    this.animate = true;
    // Clock
    this.clock = new THREE3.Clock();
    // Compositor render material
    this.material = new THREE3.ShaderMaterial();
    // Cached textures
    this.textureCache = {};
    this.state = "idle";
    // Reads colors from a texture
    this.colorReader = new ColorReader();
    // Most recent beauty texture
    this.beautyTex = new THREE3.Texture();
    // All region texture changes
    this.regionTextures = {};
    const [width, height] = resolution;
    this.resolution = resolution;
    this.updateStyles = updateStyles;
    this.animate = animate;
    this.fadeSpeed = fadeSpeed;
    this.transitionSpeed = transitionSpeed;
    this.highlightColor = new THREE3.Color(highlightColor);
    this.selectionColor = new THREE3.Color(selectionColor);
    this.renderer = new THREE3.WebGLRenderer({
      alpha: true,
      antialias: true,
      canvas: domEl,
      preserveDrawingBuffer: true
    });
    this.renderer.autoClear = false;
    this.renderer.setSize(width, height, this.updateStyles);
    this.renderer.getContext().getExtension("EXT_float_blend");
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.camera = new THREE3.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.material = new THREE3.ShaderMaterial({
      uniforms: {
        alpha: { value: 0 },
        scale: { value: scale },
        flashColor: { value: new THREE3.Color(flashColor) },
        transitionProgress: { value: 0 },
        matRegionId: { value: 0 },
        selection: { value: getEmptySelection() },
        highlight: { value: getEmptyHighlight() },
        previousHighlights: { value: populateHighlightsUniform([]) },
        beautyTex: { value: void 0 },
        uvTex: { value: void 0 },
        matteTex: { value: void 0 },
        lightTex: { value: void 0 },
        regionMaterial: {
          value: {
            texture: null,
            width: 1,
            height: 1,
            rotate: false,
            tint: new THREE3.Color(1, 1, 1)
          }
        },
        marks: { value: populateMarksUniform([]) },
        previousMarks: { value: populateMarksUniform([]) },
        lightingAdjustment: { value: DEFAULT_LIGHTING_ADJUSTMENT }
      },
      vertexShader: fullscreen_default,
      fragmentShader: compositor_default,
      depthWrite: true,
      depthTest: true,
      transparent: true
    });
    this.scene.add(
      new THREE3.Mesh(new THREE3.PlaneGeometry(2, 2, 1, 1), this.material)
    );
  }
  // Initial fade-in transition
  get alpha() {
    return this.material.uniforms.alpha.value;
  }
  set alpha(value) {
    this.material.uniforms.alpha.value = value;
  }
  // Material application transition
  get transitionProgress() {
    return this.material.uniforms.transitionProgress.value;
  }
  set transitionProgress(value) {
    this.material.uniforms.transitionProgress.value = value;
  }
  get selection() {
    return this.material.uniforms.selection.value;
  }
  set selection(value) {
    this.material.uniforms.selection.value = value;
  }
  get highlight() {
    return this.material.uniforms.highlight.value;
  }
  set highlight(value) {
    this.material.uniforms.highlight.value = value;
  }
  get previousHighlights() {
    return filterUniform(this.material.uniforms.previousHighlights);
  }
  set previousHighlights(value) {
    this.material.uniforms.previousHighlights.value = populateHighlightsUniform(value);
  }
  // Marks
  get marks() {
    return filterUniform(this.material.uniforms.marks);
  }
  set marks(value) {
    this.material.uniforms.marks.value = populateMarksUniform(value);
  }
  get previousMarks() {
    return filterUniform(this.material.uniforms.previousMarks);
  }
  set previousMarks(value) {
    this.material.uniforms.previousMarks.value = populateMarksUniform(value);
  }
  get actualResolution() {
    const { value: texture } = this.material.uniforms.matteTex;
    return texture && hasDimensions(texture) ? new THREE3.Vector2(texture.image.width, texture.image.height) : new THREE3.Vector2(0, 0);
  }
  // Lighting
  get lightingAdjustment() {
    return this.material.uniforms.lightingAdjustment.value;
  }
  set lightingAdjustment(value) {
    this.material.uniforms.lightingAdjustment.value = value;
  }
  // Dimensions
  get aspectRatio() {
    const { x, y } = this.actualResolution;
    const aspectRatio = x / y;
    return Number.isNaN(aspectRatio) ? null : aspectRatio;
  }
  get actualWidth() {
    return this.actualResolution.x;
  }
  get actualHeight() {
    return this.actualResolution.y;
  }
  /**
   * Load a new scene
   *
   * @param beautyTexture - Beauty pass image URL or buffer
   * @param matteTexture - Matte pass image URL or buffer
   * @param lightingTexture - Lighting pass image URL or buffer
   * @param uvTexture - UV pass image URL or buffer
   */
  async load(beautyTexture, matteTexture, lightingTexture, uvTexture, scale) {
    this.dispatchEvent({ type: "loading" });
    return new Promise((resolve, reject) => {
      this.state = "loading";
      this.alpha = 0;
      const exrLoader = new EXRLoader();
      exrLoader.type = THREE3.FloatType;
      const [beauty, releaseBeauty] = textureResourceToUrl(beautyTexture);
      const [lighting, releaseLighting] = textureResourceToUrl(lightingTexture);
      const [uv, releaseUv] = textureResourceToUrl(uvTexture);
      const [matte, releaseMatte] = textureResourceToUrl(matteTexture);
      this.beautyTex = new THREE3.TextureLoader().load(beauty);
      const lightingTex = new THREE3.TextureLoader().load(lighting);
      const loadExr = (url) => exrLoader.load(url, (tex) => {
        tex.minFilter = THREE3.NearestFilter;
        tex.magFilter = THREE3.NearestFilter;
      });
      const uvTex = loadExr(uv);
      const matteTex = loadExr(matte);
      this.material.uniforms.lightTex.value = lightingTex;
      this.material.uniforms.uvTex.value = uvTex;
      this.material.uniforms.matteTex.value = matteTex;
      if (scale)
        this.material.uniforms.scale.value = scale;
      this.regionTextures = {};
      this.activeRegionTexture = void 0;
      this.deselectAllRegions();
      THREE3.DefaultLoadingManager.onLoad = () => {
        this.alpha = this.animate ? 0 : 1;
        this.state = "idle";
        releaseBeauty();
        releaseLighting();
        releaseMatte();
        releaseUv();
        this.dispatchEvent({
          type: "loaded",
          textures: {
            beauty: getTextureImage(this.beautyTex),
            matte: getTextureImage(matteTex),
            lighting: getTextureImage(lightingTex),
            uv: getTextureImage(uvTex)
          }
        });
        this.generateCompositeTexture();
        resolve();
      };
      THREE3.DefaultLoadingManager.onError = (url) => reject(new Error(`SceneController: error loading asset '${url}'`));
    });
  }
  resize(width, height) {
    this.resolution = [width, height];
    this.renderer.setSize(width, height, this.updateStyles);
    if (this.state !== "loading")
      this.generateCompositeTexture();
  }
  /**
   * Apply scale value to all applied textures in the scene
   *
   * @param value scale multiplier
   */
  scale(value) {
    this.material.uniforms.scale.value = value;
    if (this.state !== "loading")
      this.generateCompositeTexture();
  }
  /**
   * Get the region ID under a given mouse coordinate
   * @param ndc Mouse position in normalized device coordinates. (Centered at 0,0. Range is -1,1.)
   * @returns The region ID under the given mouse coordinate
   */
  getRegionIdAtCoordinate({ x, y }) {
    const ndc = new THREE3.Vector2(x, y);
    const matteTex = this.material.uniforms.matteTex.value;
    if (!(matteTex instanceof THREE3.Texture))
      return null;
    const color = this.colorReader.getTextureColor(ndc, matteTex);
    const regionIdNum = new BigNumber(color[0]);
    const regionId = regionIdNum.toFixed();
    return regionId;
  }
  /**
   * Assign a region a given texture
   *
   * @param regionId Region ID code as a string
   * @param textureUrl The texture URL to apply to the region
   * @param options Region texture options, `width` and `height` are required
   */
  async setRegionTexture(regionId, textureUrl, { width, height, tint, animate, rotate }) {
    const rt = new RegionTexture(
      regionId,
      textureUrl,
      width,
      height,
      rotate ?? false,
      new THREE3.Color(tint),
      this.textureCache
    );
    this.activeRegionTexture = void 0;
    this.generateCompositeTexture();
    try {
      await rt.load();
    } catch (caught) {
      const message = `SceneController: error loading region texture '${textureUrl}', 
${ensureError(caught).message}'`;
      throw new Error(message);
    } finally {
      this.transitionProgress = animate || this.animate ? 0 : 1;
      this.regionTextures[regionId] = rt;
      this.activeRegionTexture = rt;
    }
  }
  /**
   * Reset all regions back to the original beauty texture
   */
  resetAllTextures() {
    this.regionTextures = {};
    this.activeRegionTexture = void 0;
    this.generateCompositeTexture();
  }
  /**
   * Reset a region back to the original beauty texture
   * @param regionId Region ID to reset
   */
  resetRegionTexture(regionId) {
    delete this.regionTextures[regionId];
    if (this.activeRegionTexture?.regionId === regionId) {
      this.activeRegionTexture = void 0;
    }
    this.generateCompositeTexture();
  }
  /**
   * Draw a border around a region to convey selection
   *
   * @param regionId ID of the region to highlight
   */
  selectRegion(regionId) {
    this.updateSelection(parseFloat(regionId));
  }
  /**
   * Remove the selection border from all regions
   */
  deselectAllRegions() {
    this.selection = getEmptySelection();
  }
  /**
   * Highlight a region with an overlay effect
   *
   * @param regionId ID of the region to highlight
   */
  highlightRegion(regionId) {
    this.updateHighlight(parseFloat(regionId));
  }
  /**
   * Clear all region highlights
   */
  clearRegionHighlight() {
    if (this.animate && this.highlight.regionId !== 0) {
      this.previousHighlights = [this.highlight, ...this.previousHighlights];
    }
    this.highlight = getEmptyHighlight();
  }
  /**
   * Apply a set of arbitrary color markings to a set of regions in the scene.
   * Any existing marks that had been applied to the scene will be discarded.
   *
   * NOTE: If any duplicate marks are supplied (matching `regionId`), only the
   * first mark will be applied and the duplicates will be discarded.
   *
   * @see {@link Color}
   * @see {@link ExternalMark}
   *
   * @param marks list colored marks to apply to the scene consisting of `regionId` and `color` represented as a `string` or `number`
   */
  markRegions(marks) {
    const newMarks = deduplicate(marks).map(({ color, regionId, ...rest }) => ({
      color: new THREE3.Color(color),
      regionId: parseFloat(regionId),
      ...rest
    }));
    if (!this.animate) {
      this.marks = newMarks.map((mark) => ({ alpha: 1, ...mark }));
    } else {
      const newAnimatedMarks = newMarks.map((mark) => ({ alpha: 0, ...mark }));
      this.previousMarks = [
        // Start fading out any marks that were removed
        ...difference(this.marks, newAnimatedMarks, compareColoredRegions),
        // Continue fading other marks out unless they were re-added just now
        ...difference(
          this.previousMarks,
          newAnimatedMarks,
          compareColoredRegions
        )
      ];
      const existingMarks = this.marks.concat(this.previousMarks);
      this.marks = newAnimatedMarks.map((mark) => {
        const existingMark = existingMarks.find(compareColoredRegion(mark));
        return { ...mark, alpha: existingMark?.alpha ?? 0 };
      });
    }
    this.updateHighlight();
    this.updateSelection();
  }
  /**
   * Clear any existing region color markings
   */
  clearRegionMarks() {
    this.markRegions([]);
  }
  /**
   * Trigger an image download of the current compositor scene
   *
   * @param filename filename to give the image
   * @param width desired width of image in pixels
   */
  download(filename, width) {
    const { x: actualWidth, y: actualHeight } = this.actualResolution;
    const imageWidth = width ?? actualWidth;
    const imageHeight = width ? width * (actualHeight / actualWidth) : actualHeight;
    const restoreComposite = this.material.uniforms.beautyTex.value;
    const restoreActiveRegionTexture = this.activeRegionTexture;
    this.activeRegionTexture = void 0;
    const downloadTarget = this.composite(
      this.initializeRenderTarget(imageWidth, imageHeight, 1)
    );
    this.material.uniforms.beautyTex.value = restoreComposite;
    this.activeRegionTexture = restoreActiveRegionTexture;
    const data = new Uint8Array(4 * imageHeight * imageWidth);
    this.renderer.readRenderTargetPixels(
      downloadTarget,
      0,
      0,
      imageWidth,
      imageHeight,
      data
    );
    const canvas = document.createElement("canvas");
    canvas.width = imageWidth;
    canvas.height = imageHeight;
    const context = canvas.getContext("2d");
    const imageData = context.createImageData(imageWidth, imageHeight);
    flipImageData(data, imageData);
    context.putImageData(imageData, 0, 0);
    const anchor = document.createElement("a");
    anchor.download = filename ?? "rendering.png";
    anchor.href = canvas.toDataURL();
    anchor.click();
  }
  /**
   * Redraw the scene
   */
  draw() {
    this.advanceAnimations(this.clock.getDelta());
    if (this.activeRegionTexture !== void 0) {
      const regionTexture = this.activeRegionTexture;
      this.material.uniforms.regionMaterial.value = regionTexture.asMaterial();
      this.material.uniforms.matRegionId.value = parseFloat(
        regionTexture.regionId
      );
    } else {
      this.material.uniforms.matRegionId.value = 0;
    }
    this.renderer.setRenderTarget(null);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
  }
  /**
   * Advance all applicable animations
   *
   * @param delta difference in time since last `draw`
   */
  advanceAnimations(delta) {
    this.transitionProgress = this.advanceIn(
      this.transitionProgress,
      delta,
      this.transitionSpeed
    );
    if (this.animate) {
      if (this.state === "idle" && this.alpha < 1) {
        this.alpha = this.advanceIn(this.alpha, delta, 1);
      }
      if (this.highlight.regionId !== 0 && this.highlight.alpha < 1) {
        this.highlight.alpha = this.advanceIn(this.highlight.alpha, delta);
      }
      this.marks = this.marks.map(({ alpha, ...rest }) => ({
        ...rest,
        alpha: this.advanceIn(alpha, delta)
      }));
      this.previousMarks = this.advanceManyOut(this.previousMarks, delta);
      this.previousHighlights = this.advanceManyOut(
        this.previousHighlights,
        delta
      );
    }
  }
  /**
   * Increment a value by the supplied `delta`, clamping said value at the
   * provided `ceiling`
   *
   * @param value current value
   * @param delta amount to advance (increment) value
   * @param ceiling maximum value to increase to
   *
   * @returns advanced (incremented) value
   */
  advanceIn(value, delta, speed = this.fadeSpeed, ceiling = 1) {
    const newValue = value + delta * speed;
    return newValue < ceiling ? newValue : ceiling;
  }
  /**
   * Decrement a value by the supplied `delta`, clamping said value at the
   * provided `floor`
   *
   * @param value current value
   * @param delta amount to advance (decrement) value
   * @param ceiling maximum value to increase to
   *
   * @returns advanced (decremented) value
   */
  advanceOut(value, delta, speed = this.fadeSpeed, floor = 0) {
    const newValue = value - delta * speed;
    return newValue > floor ? newValue : floor;
  }
  /**
   * Advance fade out transition for a list of items, removing the item once it
   * reaches an `alpha` of zero
   *
   * @param items list of items
   * @param delta amount to advance (decrement) `alpha` of each item
   *
   * @returns list of items with `alpha` decremented
   */
  advanceManyOut(items, delta, speed = this.fadeSpeed) {
    return items.map(({ alpha, ...rest }) => ({
      ...rest,
      alpha: this.advanceOut(alpha, delta, speed)
    })).filter(({ alpha }) => alpha > 0);
  }
  /**
   * Initialize a render target of a particular size for compositing
   *
   * @param width renderer width
   * @param height renderer height
   * @param pixelRatio renderer pixel ratio, defaults to device pixel ratio
   */
  initializeRenderTarget(width, height, pixelRatio = window.devicePixelRatio) {
    return new THREE3.WebGLRenderTarget(
      width * pixelRatio,
      height * pixelRatio,
      {
        minFilter: THREE3.NearestFilter,
        magFilter: THREE3.NearestFilter,
        format: THREE3.RGBAFormat
      }
    );
  }
  /**
   * Update selection state
   *
   * @param regionId optional `regionId` for new selection, if omitted only the
   * selection color will be updated to match any overlapping marks
   */
  updateSelection(regionId) {
    const { regionId: existingRegionId } = this.selection;
    const selectionRegionId = regionId ?? existingRegionId;
    const mark = this.marks.find((m) => m.regionId === selectionRegionId);
    this.selection = {
      regionId: selectionRegionId,
      strength: mark ? 0.9 : 0.5,
      color: mark ? mark.color : this.selectionColor
    };
  }
  /**
   * Update highlight state
   *
   * @param regionId optional `regionId` for new highlight, if omitted only the
   * highlight color will be updated to match any overlapping marks
   */
  updateHighlight(regionId) {
    const { regionId: existingHighlightId } = this.highlight;
    const highlightRegionId = regionId ?? existingHighlightId;
    const mark = this.marks.find((m) => m.regionId === highlightRegionId);
    const highlight = {
      regionId: highlightRegionId,
      alpha: 1,
      color: mark?.color ?? this.highlightColor
    };
    if (!this.animate) {
      this.highlight = highlight;
      return;
    }
    const isCurrentHighlight = compareColoredRegion(highlight);
    const existingHighlight = this.previousHighlights.find(isCurrentHighlight);
    if (existingHighlight) {
      this.previousHighlights = this.previousHighlights.filter(
        (h) => !isCurrentHighlight(h)
      );
    }
    if (this.highlight.regionId !== 0) {
      this.previousHighlights = [this.highlight, ...this.previousHighlights];
    }
    this.highlight = {
      ...highlight,
      alpha: existingHighlight?.alpha ?? 0
    };
  }
  /**
   * Draws all the layers of the final composite texture
   */
  generateCompositeTexture() {
    const [width, height] = this.resolution;
    this.material.uniforms.beautyTex.value = this.composite(
      this.initializeRenderTarget(width, height)
    ).texture;
  }
  /**
   * Draws all the layers of the final composite texture
   */
  composite(target) {
    let foreground = target;
    let background = target.clone();
    const origSelection = this.selection;
    const origTransitionProgress = this.transitionProgress;
    const origMarks = this.marks;
    const origAlpha = this.alpha;
    this.deselectAllRegions();
    this.transitionProgress = 1;
    this.marks = [];
    this.alpha = 1;
    this.material.uniforms.beautyTex.value = this.beautyTex;
    this.material.uniforms.matRegionId.value = 0;
    this.renderer.setRenderTarget(foreground);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    Object.entries(this.regionTextures).forEach(([regionId, regionTexture]) => {
      if (regionId === this.activeRegionTexture?.regionId) {
        return;
      }
      this.material.uniforms.regionMaterial.value = regionTexture.asMaterial();
      this.material.uniforms.matRegionId.value = parseFloat(
        regionTexture.regionId
      );
      const temporary = foreground;
      foreground = background;
      background = temporary;
      this.material.uniforms.beautyTex.value = background.texture;
      this.renderer.setRenderTarget(foreground);
      this.renderer.clear();
      this.renderer.render(this.scene, this.camera);
    });
    this.selection = origSelection;
    this.transitionProgress = origTransitionProgress;
    this.marks = origMarks;
    this.alpha = origAlpha;
    return foreground;
  }
};

// src/Compositor/imageHelpers.ts
var filterHistogram = (histogram, threshold = 1) => {
  const sum = histogram.reduce((acc, value) => acc + value, 0);
  const mean = histogram.reduce((acc, value, index) => acc + value * index, 0) / sum;
  const standardDeviation = Math.sqrt(
    histogram.reduce(
      (acc, value, index) => acc + value * (index - mean) ** 2,
      0
    ) / sum
  );
  const filtered = histogram.map(
    (value, index) => Math.abs(index - mean) > threshold * standardDeviation ? 0 : value
  );
  return filtered;
};
var generateRGBHistogram = (imageData) => {
  const red = new Array(256).fill(0);
  const green = new Array(256).fill(0);
  const blue = new Array(256).fill(0);
  for (let i = 0; i < imageData.length; i += 4) {
    const r = imageData[i];
    const g = imageData[i + 1];
    const b = imageData[i + 2];
    red[r] += 1;
    green[g] += 1;
    blue[b] += 1;
  }
  const combined = new Array(256).fill(0);
  for (let i = 0; i < 256; i++)
    combined[i] = red[i] + green[i] + blue[i];
  return combined;
};
var getImageData = (image) => {
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext("2d");
  context.drawImage(image, 0, 0);
  const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  return new Uint8Array(imageData.data);
};

// src/Compositor/Compositor.ts
var Compositor = class extends EventDispatcher {
  /**
   * Constructor
   *
   * @param canvasEl DOM canvas element to render into
   */
  constructor(canvasEl, { fixLighting = true, ...options } = {}) {
    super();
    this._shadowExclusion = 1;
    this._shadowThreshold = 0.5;
    this.canvasEl = canvasEl;
    this.sceneControllerOptions = options;
    const { width } = canvasEl;
    const { height } = canvasEl;
    this.resolution = new THREE4.Vector2(width, height);
    this.sceneController = new SceneController(
      canvasEl,
      [width, height],
      this.sceneControllerOptions
    );
    this.updateLightingAjustment({ enabled: fixLighting }, false);
    this.sceneController.addEventListener(
      "loading",
      (event) => this.dispatchEvent(event)
    );
    this.sceneController.addEventListener(
      "loaded",
      this.onSceneLoaded.bind(this)
    );
    canvasEl.addEventListener("click", this.onClick.bind(this), false);
    canvasEl.addEventListener("mousemove", this.onMouseMove.bind(this), false);
    canvasEl.addEventListener("mousedown", this.onMouseDown.bind(this), false);
    canvasEl.addEventListener("mouseup", this.onMouseUp.bind(this), false);
    this.tick();
  }
  get aspectRatio() {
    return this.sceneController.aspectRatio;
  }
  get actualWidth() {
    return this.sceneController.actualWidth;
  }
  get actualHeight() {
    return this.sceneController.actualHeight;
  }
  get lightingAdjustment() {
    return this.sceneController.lightingAdjustment;
  }
  get shadowThreshold() {
    return this._shadowThreshold;
  }
  set shadowThreshold(value) {
    this._shadowThreshold = value;
    if (this.sceneController.state === "idle") {
      this.applyAutoLightingAdjustment();
    }
  }
  get shadowExclusion() {
    return this._shadowExclusion;
  }
  set shadowExclusion(value) {
    this._shadowExclusion = value;
    if (this.sceneController.state === "idle") {
      this.applyAutoLightingAdjustment();
    }
  }
  set lightingData(value) {
    this._lightingData = value;
    this.dispatchEvent({ type: "lighting-data", data: value });
  }
  get lightingData() {
    return this._lightingData;
  }
  /**
   * Resize the compositor
   *
   * @param width New width
   * @param height New height
   */
  resize(width, height) {
    this.resolution = new THREE4.Vector2(width, height);
    this.sceneController.resize(width, height);
  }
  /**
   * Apply scale value to all applied textures in the scene
   *
   * @param value scale multiplier
   */
  scale(value) {
    this.sceneController.scale(value);
  }
  /**
   * Load a new scene
   *
   * @param beautyTexture - Beauty pass image URL or buffer
   * @param matteTexture - Matte pass image URL or buffer
   * @param lightingTexture - Lighting pass image URL or buffer
   * @param uvTexture - UV pass image URL or buffer
   */
  async load(beautyTexture, matteTexture, lightingTexture, uvTexture, scale) {
    return this.sceneController.load(
      beautyTexture,
      matteTexture,
      lightingTexture,
      uvTexture,
      scale
    );
  }
  /**
   * Trigger an image download of the current compositor scene
   *
   * @param filename filename to give the image
   * @param width desired width of image in pixels
   */
  download(filename, width) {
    return this.sceneController.download(filename, width);
  }
  /**
   * Assign an event handler for when the mouse hovers over a region
   *
   * @param handler Mouse-over event handler
   */
  setClickHandler(handler) {
    this.clickHandler = handler;
  }
  /**
   * Assign an event handler for when the mouse hovers over a region
   *
   * @param handler Mouse-over event handler
   */
  setMouseMoveHandler(handler) {
    this.mouseMoveHandler = handler;
  }
  /**
   * Assign an event handler for when the mouse is pressed down over a region
   *
   * @param handler Mouse-down event handler
   */
  setMouseDownHandler(handler) {
    this.mouseDownHandler = handler;
  }
  /**
   * Assign an event handler for when the mouse is released over a region
   *
   * @param handler Mouse-up event handler
   */
  setMouseUpHandler(handler) {
    this.mouseUpHandler = handler;
  }
  /**
   * Assign a region a given texture
   *
   * @param regionId Region ID code as a string
   * @param textureUrl The texture URL to apply to the region
   * @param options Region texture options, `width` and `height` are required
   */
  async setRegionTexture(regionId, textureUrl, options) {
    return this.sceneController.setRegionTexture(regionId, textureUrl, options);
  }
  /**
   * Reset all regions back to the original beauty texture
   */
  resetAllTextures() {
    this.sceneController.resetAllTextures();
  }
  /**
   * Reset a region back to the original beauty texture
   *
   * @param regionId Region ID to reset
   */
  resetRegionTexture(regionId) {
    this.sceneController.resetRegionTexture(regionId);
  }
  /**
   * Draw a border around a region to convey selection
   *
   * @param regionId ID of the region to highlight
   */
  selectRegion(regionId) {
    this.sceneController.selectRegion(regionId);
  }
  /**
   * Remove the selection border from all regions
   */
  deselectAllRegions() {
    this.sceneController.deselectAllRegions();
  }
  /**
   * Highlight a region with an overlay effect
   *
   * @param regionId ID of the region to highlight
   */
  highlightRegion(regionId) {
    this.sceneController.highlightRegion(regionId);
  }
  /**
   * Clear all region highlights
   */
  clearRegionHighlight() {
    this.sceneController.clearRegionHighlight();
  }
  /**
   * Apply a set of arbitrary color markings to a set of regions in the scene.
   * Any existing marks that had been applied to the scene will be discarded.
   *
   * NOTE: If any duplicate marks are supplied (matching `regionId`), only the
   * first mark will be applied and the duplicates will be discarded.
   *
   * @see {@link Color}
   * @see {@link Mark}
   *
   * @param marks list colored marks to apply to the scene consisting of `regionId` and `color` represented as a `string` or `number`
   */
  markRegions(marks) {
    this.sceneController.markRegions(marks);
  }
  updateLightingAjustment(adjustment, recomposite = true) {
    const updated = {
      ...this.sceneController.lightingAdjustment,
      ...adjustment
    };
    this.sceneController.lightingAdjustment = updated;
    if (recomposite)
      this.sceneController.generateCompositeTexture();
    this.dispatchEvent({ type: "lighting-adjustment", adjustment: updated });
  }
  resetLightingAdjustment() {
    this.sceneController.lightingAdjustment = DEFAULT_LIGHTING_ADJUSTMENT;
  }
  /**
   * Clear any existing region color markings
   */
  clearRegionMarks() {
    this.sceneController.markRegions([]);
  }
  /**
   * Given a mouse event, return its normalized device coordinates (Centered at 0,0. Range is -1,1)
   *
   * @param ev Mouse event
   * @returns Mouse position in normalized device coordinates
   */
  mouseEventToNDC(ev) {
    const windowX = ev.clientX;
    const windowY = ev.clientY;
    const { left, top, height } = this.canvasEl.getBoundingClientRect();
    const canvasX = windowX - left;
    const canvasY = height + top - windowY;
    const hw = this.canvasEl.clientWidth / 2;
    const hh = this.canvasEl.clientHeight / 2;
    const deviceX = canvasX - hw;
    const deviceY = canvasY - hh;
    const ndcX = deviceX / this.resolution.x * 2;
    const ndcY = deviceY / this.resolution.y * 2;
    const ndc = new THREE4.Vector2(ndcX, ndcY);
    return ndc;
  }
  /**
   * Mouse move handler
   *
   * @param ev Mouse event
   */
  onClick(ev) {
    if (!this.clickHandler)
      return;
    const ndc = toSimpleCoordinates(this.mouseEventToNDC(ev));
    const regionId = this.sceneController.getRegionIdAtCoordinate(ndc);
    if (!regionId)
      return;
    this.clickHandler(regionId, ndc);
  }
  /**
   * Mouse move handler
   *
   * @param ev Mouse event
   */
  onMouseMove(ev) {
    if (!this.mouseMoveHandler)
      return;
    const ndc = toSimpleCoordinates(this.mouseEventToNDC(ev));
    const regionId = this.sceneController.getRegionIdAtCoordinate(ndc);
    if (!regionId)
      return;
    this.mouseMoveHandler(regionId, ndc);
  }
  /**
   * Mouse down handler
   *
   * @param ev Mouse event
   */
  onMouseDown(ev) {
    if (!this.mouseDownHandler)
      return;
    const ndc = toSimpleCoordinates(this.mouseEventToNDC(ev));
    const regionId = this.sceneController.getRegionIdAtCoordinate(ndc);
    if (!regionId)
      return;
    this.mouseDownHandler(regionId, ndc);
  }
  /**
   * Mouse up handler
   *
   * @param ev Mouse event
   */
  onMouseUp(ev) {
    if (!this.mouseUpHandler)
      return;
    const ndc = toSimpleCoordinates(this.mouseEventToNDC(ev));
    const regionId = this.sceneController.getRegionIdAtCoordinate(ndc);
    if (!regionId)
      return;
    this.mouseUpHandler(regionId, ndc);
  }
  onSceneLoaded(event) {
    this.lightingImage = event.textures.lighting;
    this.applyAutoLightingAdjustment(false);
    this.dispatchEvent(event);
  }
  /**
   * Automatically exclude some shadows from the lighting texture layer in order
   * to make applied product color more "true" to the original color value.
   *
   * This is accomplished with the following steps:
   * 1. Generate a light intensity histogram (similar to Photoshop's levels tool)
   * 2. Exclude extreme, outlier, values from the histogram (there are usually a
   *    lot of pure whites and darks)
   * 3. Starting from the shadow side of the histogram (the right or end), find
   *    the first value that is at least `this.shadowThreshold`% of the peak
   *    histogram value
   * 4. Exclude shadows above said value from the lighting texture layer by
   *    using said value as the "input maximum" parameter in the levels
   *    adjustment
   *
   *  Also, set all of the values used to compute this adjustment as
   * `this.lightingData` for the purposes of visibility and debugging
   *
   * @param recomposite whether to regenerate the composite texture after
   * updating the lighting adjustment
   */
  applyAutoLightingAdjustment(recomposite = true) {
    if (!this.lightingImage) {
      console.warn(
        "Compositor: failed to load lighting texture for auto lighting adjustment"
      );
      return;
    }
    const lightingImageData = getImageData(this.lightingImage);
    const histogramRaw = generateRGBHistogram(lightingImageData);
    const histogram = filterHistogram(histogramRaw, this.shadowExclusion);
    const peakLevel = histogram.reduce((a, b) => Math.max(a, b));
    const reversed = histogram.slice().reverse();
    const threshold = reversed.findIndex(
      (l) => l >= peakLevel - peakLevel * this.shadowThreshold
    );
    const inputMaxLevel = histogram.length - threshold;
    const inputMax = inputMaxLevel / histogram.length;
    this.lightingData = {
      histogram,
      histogramRaw,
      peakLevel,
      inputMax,
      markers: {
        threshold: inputMaxLevel,
        high: histogram.length - reversed.findIndex((l) => l > 0),
        low: histogram.findIndex((l) => l > 0),
        peak: histogram.findIndex((l) => l === peakLevel)
      }
    };
    this.updateLightingAjustment({ inputMax }, recomposite);
  }
  /**
   * Main update loop
   */
  tick() {
    this.sceneController.draw();
    requestAnimationFrame(this.tick.bind(this));
  }
};
export {
  Compositor,
  DEFAULT_ASPECT_RATIO,
  DEFAULT_COLOR,
  DEFAULT_LIGHTING_ADJUSTMENT,
  DEFAULT_SCALE,
  SceneController
};
/*! Bundled license information:

three/examples/jsm/libs/fflate.module.js:
  (*!
  fflate - fast JavaScript compression/decompression
  <https://101arrowz.github.io/fflate>
  Licensed under MIT. https://github.com/101arrowz/fflate/blob/master/LICENSE
  version 0.6.9
  *)
*/
//# sourceMappingURL=index.mjs.map